import { requireAuth } from './auth.js';
requireAuth();
import {
  HARPOON_FIRE_POWER_MW,
  HARPOON_SCAN_POWER_MW,
  LIGHTS_POWER_USAGE_PER_MINUTE,
  PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION,
  SIMULATION_INTERVAL_MS,
  STORAGE_CAPACITY,
  DOCKING_ACTIVATION_RANGE_METERS,
  ROOM_HELP_TEXT,
  GAME_START_COUNTDOWN_MS,
  getSonarScanCostMultiplier,
  CAST_LATENCY_STALE_MS,
  MINING_SCAN_UPGRADE_ID,
  MINING_LIGHTS_UPGRADE_ID
} from '../shared/constants.js';
import { applyEffectsVolume, createMixedAudioContext, DEFAULT_MIX, getMixVolume, setMixVolume, trackEffectsVolume } from './audioMix.js';
import { playEmpBlast, styleEmpBlastOverlay } from './effects/empBlast.js';
import { createVoiceLines } from './effects/voiceLines.js';
import { setLogReplayHandler } from './narrativeLogs.js';
import { createHullLeakOverlay, type HullLeakOverlay } from './effects/hullLeakOverlay.js';
import { playLeakBurst, setLeakHissLevel } from './effects/hullLeakAudio.js';
import { countHullBreaches, isRoleInBreachedZone, leakSprayCount } from '../shared/submarineLayout.js';
import { EVENTS } from '../shared/events.js';
import type {
  CollisionImpactPayload,
  ControlInput,
  GameSnapshot,
  HostEventLogEntryPayload,
  MarkerSoundTriggeredPayload,
  PowerFailurePayload,
  Role,
  RoleAssignedPayload,
  RoomState,
  SonarPulsePayload,
  StorageActionPayload,
  SubmarineState,
  HullZoneId
} from '../shared/types.js';
import { renderMetricChart } from './metricChart.js';
import { triggerSonarPulseEffects } from './effects/sonarPulseEffects.js';
import { startAtmosphericCreaks } from './effects/randomCreaks.js';
import { HarpoonCameraView } from './rendering/HarpoonCameraView.js';
import { updateDroneWarnings } from './droneWarnings.js';
import { BottomHullCameraView } from './rendering/BottomHullCameraView.js';
import {
  DEFAULT_RENDER_TUNING,
  normalizeRenderTuning,
  type RenderTuning
} from './rendering/renderTuning.js';
import {
  loadRenderTuningWithFallback,
  saveRenderTuningToLocalStorage
} from './rendering/renderTuningPersistence.js';
import { DEFAULT_MAP_ID, getMapById, getMapByMissionId, loadCustomMapDefinition, loadCustomMapCollisionMesh } from './rendering/maps/index.js';
import { sampleMapHeight } from './rendering/maps/terrainSampling.js';
import {
  normalizeHeadingDegrees,
  salvageTargetToMapPoint,
  submarineToMapPoint
} from '../shared/spatial.js';
import { getDockingDistanceMeters } from '../shared/docking.js';
import { mapPointToCanvasPoint } from '../shared/viewProjection.js';
import { type GraphicsQuality, detectDeviceType, getRecommendedPresetId, getPresetById, getPresetFor, getPresetQualityLevel, stepDownQualityLevel, CASTING_LOCAL_FPS_CAP, CASTING_LOCAL_RELIEF_FPS, type DevicePreset, type DeviceType, type QualityLevel } from './rendering/quality.js';
import { preloadCoreModels } from './rendering/modelLoader.js';
import { clearStoredSession, clearStoredSessionToken, loadStoredSession, persistSession, state as controllerState } from './controllerState.js';
import { createControllerNetwork, type ControllerNetwork } from './controllerNetwork.js';
import { CAST_PRESSURE_CHANNEL } from './rendering/adaptiveQuality.js';
import { mountCastSetup, type CastSetupAdapter } from './castSetup.js';
import { createWebCastSender } from './webCastSender.js';
import { byId, setHidden, setTextContent } from './controllerDom.js';
import { SUBSURFACE_SCAN_COST } from './rooms/NavigationRoomController.js';
import { clamp, createCard, createPlusMinusControl, resourceReadoutVariant, throttle, type ReadoutVariant } from './controllerUiHelpers.js';
import {
  appendControllerHostEventLogEntry,
  closeEventLogPanel,
  closeTechTreePanel,
  openEventLogPanel,
  openTechTreePanel,
  showQuickToast
} from './controllerPanels.js';
import {
  createRoleButton,
  handleRoleTap,
  renderBatteryControls,
  renderBallastControls,
  renderCaptainControls,
  updateCaptainPanel as updateCaptainConsole,
  renderDroneControls,
  renderEngineerControls,
  updateEngineerPanel,
  disposeEngineerView,
  renderEngineControls,
  renderNavigationControls,
  renderCommsControls,
  renderLifeSupportControls,
  renderPilotControls,
  renderStorageControls,
  renderSonarControls,
  renderHarpoonControls,
  renderWeaponsControls,
  renderWeaponsAftControls,
  renderPeriscopeControls,
  tryHarpoonAction,
  tryHarpoonScan,
  updatePowerAndLightUi,
  setDockingUiElements,
  updatePilotPanel,
  updateBallastPanel,
  stopBatteryAnimation,
  getDockingCameraOriginMapPoint,
  describeDockingApproach,
  getSonarRangeMeters,
  getSonarPulsePowerMw,
  appendCaptainNavigationHits,
  captureSonarPingHitsForNavigation,
  getSonarScanType,
  updateSonarDepthDial,
  setSonarRoomState,
  incrementSonarRange,
  decrementSonarRange,
  setSonarRangeUpdateCallback,
  createPeriscopeLightsButton,
  getRevealedNodes,
  resetNodeRevealState,
  restoreNodeRevealState,
  loadTileJsonForMap,
  setControllerNetwork,
  setControllerEmit,
  setGraphicsQuality,
  setUserFpsOverride,
  setControllerRenderTuning,
  setControllerRoomState,
  notifyCaptainLogUnlocked,
  updateStorageRoomState,
  setControllerSnapshot,
  setActiveMapGetter,
  setRoleMetaGetter,
  bindHarpoonUiElements,
  clearHarpoonUiElements,
  disposeDroneControls,
  disposeWeaponsControls,
  saveGameWithFeedback
} from './controllerRoleControls.js';
import { disposeRoomMapViews, initMapViewForRole, renderRoleButtons, renderRoomMap, toggleRoomMapPanel } from './controllerRoomMap.js';
import { createFacilityUpgradesController } from './rooms/FacilityUpgradesController.js';
import { createFacilityShopController } from './rooms/FacilityShopController.js';
import { createFacilityMissionsController } from './rooms/FacilityMissionsController.js';
import { getStorageSpotsUsed } from './rooms/StorageRoomController.js';
import { createFacilityRepairController } from './rooms/FacilityRepairController.js';
import { initializeControllerEffects, triggerPowerFailureLightsFlicker, updateTemperatureEffects } from './controllerEffects.js';
import { ALERT_RESOURCES, getSubmarineAlertSeverities, RESOURCE_ALERT_LABEL, RESOURCE_OWNER_ROLE } from '../shared/alerts.js';
import { t, tOpt, getLanguage, setLanguage, LANGUAGE_NAMES, type Lang } from './i18n.js';

let network: ControllerNetwork | null = null;

// Translate static HTML elements on page load
((): void => {
  const set = (id: string, key: string): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = t(key);
  };
  set('assignedRole', 'unassigned');
  set('lobbyRoomMeta', 'no_room_joined');
  set('roomMapTitle', 'rooms_title');
  set('controllerRoomHelpTitle', 'room_help');
  set('roomHelpSubtitle', 'room_help_subtitle');
  set('controllerRoomHelpDescription', 'room_help_body');
  set('controllerRoomMapStatus', 'select_room');
})();

const showControllerQuickToast = (message: string): void => showQuickToast(controllerQuickToast, message);
const openControllerEventLogPanel = (): void => openEventLogPanel(controllerEventLogPanel, controllerEventLogList, controllerEventLogButton);
const closeControllerEventLogPanel = (): void => closeEventLogPanel(controllerEventLogPanel);
const appendControllerHostEventLogEntrySafe = (payload: HostEventLogEntryPayload): void => {
  appendControllerHostEventLogEntry(payload, controllerEventLogPanel, controllerEventLogList, controllerEventLogBadge, controllerEventLogButton);
};

const engineAlertStyle = document.createElement('style');
engineAlertStyle.textContent = `
  @keyframes engineAlertFlash {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
  }
`;
document.head.appendChild(engineAlertStyle);

const roomInput = byId<HTMLInputElement>('roomInput');
const nameInput = byId<HTMLInputElement>('nameInput');
const joinButton = byId<HTMLButtonElement>('joinButton');
const nextStepButton = byId<HTMLButtonElement>('nextStepButton');
const backStepButton = byId<HTMLButtonElement>('backStepButton');
const lobbyStep1 = byId<HTMLElement>('lobbyStep1');
const lobbyStep2 = byId<HTMLElement>('lobbyStep2');
const lobbyStep3 = byId<HTMLElement>('lobbyStep3');
const codeConfirmLabel = byId<HTMLElement>('codeConfirmLabel');
const waitingRoomCode = byId<HTMLElement>('waitingRoomCode');
const waitingRoleDisplay = byId<HTMLElement>('waitingRoleDisplay');
const lobbyOpenRoomsButton = byId<HTMLButtonElement>('lobbyOpenRoomsButton');
const lobbyLeaveButton = byId<HTMLButtonElement>('lobbyLeaveButton');
const controllerStatus = byId<HTMLParagraphElement>('controllerStatus');
const controllerRuntimeStatus = byId<HTMLParagraphElement>('controllerRuntimeStatus');
const lobbyRoleButtons = byId<HTMLDivElement>('lobbyRoleButtons');
const controllerRoleTabs = byId<HTMLDivElement>('controllerRoleTabs');
const assignedRole = byId<HTMLElement>('assignedRole');
const lobbyRoomMeta = byId<HTMLElement>('lobbyRoomMeta');
const controls = byId<HTMLDivElement>('controls');

const facilityUpgrades = createFacilityUpgradesController({ controls, getControllerNetwork: () => network });
const facilityShop = createFacilityShopController({ controls, getControllerNetwork: () => network });
const facilityMissions = createFacilityMissionsController({ controls, getControllerNetwork: () => network });
const facilityRepair = createFacilityRepairController({ controls, getControllerNetwork: () => network });

const reconnectBanner = byId<HTMLDivElement>('controllerReconnectBanner');
const controllerLobbyScreen = byId<HTMLElement>('controllerLobbyScreen');
const controllerMissionScreen = byId<HTMLElement>('controllerMissionScreen');
const controllerPowerFailureBanner = byId<HTMLElement>('controllerPowerFailureBanner');
const controllerReserveAirBanner = byId<HTMLElement>('controllerReserveAirBanner');
const controllerResourceAlertBanner = byId<HTMLElement>('controllerResourceAlertBanner');
let lastPowerFailureState = false;
const controllerHostClosedBanner = byId<HTMLElement>('controllerHostClosedBanner');
const controllerEmpFlashOverlay = document.createElement('div');
styleEmpBlastOverlay(controllerEmpFlashOverlay);
document.body.appendChild(controllerEmpFlashOverlay);

const controllerFloodOverlay = document.createElement('div');
controllerFloodOverlay.className = 'controller-flood-overlay';
const controllerFloodFill = document.createElement('div');
controllerFloodFill.className = 'controller-flood-fill';
const controllerFloodWaterline = document.createElement('div');
controllerFloodWaterline.className = 'controller-flood-waterline';
controllerFloodFill.appendChild(controllerFloodWaterline);
controllerFloodOverlay.appendChild(controllerFloodFill);
document.body.appendChild(controllerFloodOverlay);

const updateFloodOverlay = (floodPercent: number): void => {
  const visible = floodPercent > 2;
  controllerFloodOverlay.classList.toggle('flood-visible', visible);
  if (visible) {
    controllerFloodFill.style.setProperty('--flood-fill-height', `${floodPercent}%`);
    controllerFloodFill.classList.toggle('flood-critical', floodPercent >= 50);
  }
};
initializeControllerEffects();
// const controllerHullCracks = byId<HTMLDivElement>('controllerHullCracks');
const controllerWaterEffect = byId<HTMLCanvasElement>('controllerWaterEffect');

import { WaterFillEffect } from './effects/waterFillEffect.js';
import { playCollisionImpactSound as playSharedCollisionImpactSound } from './effects/collisionImpactSound.js';
let waterLevel = 0; // 0 (empty) to 1 (full)
// radians
const controllerSettingsButton = document.querySelector<HTMLButtonElement>('#controllerSettingsButton');
const controllerSettingsPanel = document.querySelector<HTMLElement>('#controllerSettingsPanel');
const controllerSettingsClose = document.querySelector<HTMLButtonElement>('#controllerSettingsClose');
const controllerCastButton = document.querySelector<HTMLButtonElement>('#controllerCastButton');
const controllerCastPanel = document.querySelector<HTMLElement>('#controllerCastPanel');
const controllerCastClose = document.querySelector<HTMLButtonElement>('#controllerCastClose');
const controllerCastSetupMount = document.querySelector<HTMLElement>('#controllerCastSetupMount');

const controllerRoomHelpButton = document.querySelector<HTMLButtonElement>('#controllerRoomHelpButton');
const controllerRoomHelpPanel = document.querySelector<HTMLElement>('#controllerRoomHelpPanel');
const controllerRoomHelpClose = document.querySelector<HTMLButtonElement>('#controllerRoomHelpClose');
const controllerRoomHelpTitle = document.querySelector<HTMLElement>('#controllerRoomHelpTitle');
const controllerRoomHelpDescription = document.querySelector<HTMLElement>('#controllerRoomHelpDescription');
const controllerEventLogButton = document.querySelector<HTMLButtonElement>('#controllerEventLogButton');
const controllerEventLogBadge = document.querySelector<HTMLSpanElement>('#controllerEventLogBadge');
const controllerEventLogPanel = document.querySelector<HTMLElement>('#controllerEventLogPanel');
const controllerEventLogClose = document.querySelector<HTMLButtonElement>('#controllerEventLogClose');
const controllerEventLogList = document.querySelector<HTMLUListElement>('#controllerEventLogList');
const controllerTechTreePanel = document.querySelector<HTMLElement>('#controllerTechTreePanel');
const controllerTechTreeClose = document.querySelector<HTMLButtonElement>('#controllerTechTreeClose');
const controllerTechTreeBody = document.querySelector<HTMLElement>('#controllerTechTreeBody');
const controllerQuickToast = document.querySelector<HTMLElement>('#controllerQuickToast');
const controllerMissionSummary = document.querySelector<HTMLElement>('#controllerMissionSummary');
const controllerSummaryReason = document.querySelector<HTMLElement>('#controllerSummaryReason');
const controllerSummaryDuration = document.querySelector<HTMLElement>('#controllerSummaryDuration');
const controllerSummaryDistance = document.querySelector<HTMLElement>('#controllerSummaryDistance');
const controllerSummaryResources = document.querySelector<HTMLElement>('#controllerSummaryResources');
const controllerSummarySonar = document.querySelector<HTMLElement>('#controllerSummarySonar');
const controllerSummaryRestartBtn = document.querySelector<HTMLButtonElement>('#controllerSummaryRestartBtn');
const controllerSummaryDismissBtn = document.querySelector<HTMLButtonElement>('#controllerSummaryDismissBtn');
const controllerSummaryChart = document.querySelector<HTMLCanvasElement>('#controllerSummaryChart');
const controllerFullscreenButton = byId<HTMLButtonElement>('controllerFullscreenButton');
const controllerExitGameButton = byId<HTMLButtonElement>('controllerExitGameButton');
const controllerExitGameDialog = byId<HTMLElement>('controllerExitGameDialog');
const controllerExitGameCancel = byId<HTMLButtonElement>('controllerExitGameCancel');
const controllerHeaderMenuToggle = byId<HTMLButtonElement>('controllerHeaderMenuToggle');
const controllerHeaderMenu = byId<HTMLElement>('controllerHeaderMenu');
const controllerMenuRoomCodeValue = document.querySelector<HTMLElement>('#controllerMenuRoomCodeValue');
const controllerExitLeaveRoomButton = document.querySelector<HTMLButtonElement>('#controllerExitLeaveRoom');
const controllerExitRestartButton = document.querySelector<HTMLButtonElement>('#controllerExitRestart');
const controllerExitEndGameButton = document.querySelector<HTMLButtonElement>('#controllerExitEndGame');
const controllerRejoinDialog = document.querySelector<HTMLElement>('#controllerRejoinDialog');
const controllerRejoinDialogTitle = document.querySelector<HTMLElement>('#ctrlRejoinDialogTitle');
const controllerRejoinConfirm = document.querySelector<HTMLButtonElement>('#controllerRejoinConfirm');
const controllerRejoinDecline = document.querySelector<HTMLButtonElement>('#controllerRejoinDecline');
const roomMenuButton = document.querySelector<HTMLButtonElement>('#roomMenuButton');
const roomMapPanel = document.querySelector<HTMLElement>('#controllerRoomMapPanel');
const roomMapCloseButton = document.querySelector<HTMLButtonElement>('#controllerRoomMapClose');
const roomMapGrid = document.querySelector<HTMLDivElement>('#controllerRoomMapGrid');
const roomMapTabs = document.querySelector<HTMLDivElement>('#controllerRoomMapTabs');
const roomMapStatus = document.querySelector<HTMLElement>('#controllerRoomMapStatus');
const controllerRoomRepairButton = document.querySelector<HTMLButtonElement>('#controllerRoomRepairButton');
const ctrlRoomHealthFill = document.querySelector<HTMLElement>('#ctrlRoomHealthFill');
const ctrlRoomHealthValue = document.querySelector<HTMLElement>('#ctrlRoomHealthValue');
const ctrlZoneLeftTop = byId<HTMLDivElement>('ctrlZoneLeftTop');
const ctrlZoneLeftBottom = byId<HTMLDivElement>('ctrlZoneLeftBottom');
const ctrlZoneRightAux = byId<HTMLDivElement>('ctrlZoneRightAux');
let autoOpenRoomMenuOnJoin = false;
const settingsDeviceTabs = document.querySelector<HTMLElement>('#settingsDeviceTabs');
const settingsQualityRow = document.querySelector<HTMLElement>('#settingsQualityRow');
const settingsAntialias = document.querySelector<HTMLSelectElement>('#settingsAntialias');
const settingsFps = document.querySelector<HTMLSelectElement>('#settingsFps');
const settingsVibration = document.querySelector<HTMLSelectElement>('#settingsVibration');
const settingsDeviceAudio = document.querySelector<HTMLSelectElement>('#settingsDeviceAudio');
const settingsTvMusic = document.querySelector<HTMLInputElement>('#settingsTvMusic');
const settingsTvEffects = document.querySelector<HTMLInputElement>('#settingsTvEffects');
const settingsPhoneVolume = document.querySelector<HTMLInputElement>('#settingsPhoneVolume');
const settingsLanguage = document.querySelector<HTMLSelectElement>('#settingsLanguage');

// Populate language selector
if (settingsLanguage) {
  for (const [code, name] of Object.entries(LANGUAGE_NAMES)) {
    const opt = document.createElement('option');
    opt.value = code;
    opt.textContent = name;
    settingsLanguage.append(opt);
  }
  settingsLanguage.value = getLanguage();
}

const GRAPHICS_QUALITY_KEY = 'abyss-link-graphics-quality';
const ANTIALIAS_KEY = 'abyss-link-antialias';
const PIXEL_RATIO_SCALE_KEY = 'abyss-link-pixel-ratio-scale';
const DEVICE_PRESET_KEY = 'abyss-link-device-preset';
const PRESET_FPS_KEY = 'abyss-link-preset-fps';
const VIBRATION_KEY = 'abyss-link-vibration';
const DEVICE_AUDIO_KEY = 'abyss-link-device-audio';

type VibrationLevel = 'high' | 'low' | 'off';

let vibrationLevel: VibrationLevel = (() => {
  const v = window.localStorage.getItem(VIBRATION_KEY);
  return v === 'low' || v === 'off' ? v : 'high';
})();

let deviceAudioEnabled: boolean = window.localStorage.getItem(DEVICE_AUDIO_KEY) !== 'off';

let hullAudioContext: AudioContext | null = null;
let lastCreakAtMs = 0;
let previousCrackSeverity = 0;
const playedHullWarnings = new Set<number>();
let pilotEngineLoop: {
  source: AudioBufferSourceNode;
  gain: GainNode;
  filter: BiquadFilterNode;
  active: boolean;
} | null = null;
let pilotEngineFadeTimer: number | null = null;
let storageProcessingAudio: HTMLAudioElement | null = null;
let lastEmpEventTick: number | null = null;
let currentStorageProcessingActive = false;
// --- Device preset initialisation ---
const _detectedDeviceType = detectDeviceType();
const _recommendedPresetId = getRecommendedPresetId(_detectedDeviceType);
const _storedPresetId = window.localStorage.getItem(DEVICE_PRESET_KEY);
let activePreset: DevicePreset = ((_storedPresetId ? getPresetById(_storedPresetId) : undefined) ?? getPresetById(_recommendedPresetId))!;

// Reflect the active preset in the device tabs, quality buttons and advanced controls.
const renderGraphicsSettingsUi = (): void => {
  const device = activePreset.deviceType;
  const level = getPresetQualityLevel(activePreset);
  settingsDeviceTabs?.querySelectorAll('button').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.device === device);
    btn.setAttribute('aria-selected', btn.dataset.device === device ? 'true' : 'false');
  });
  settingsQualityRow?.querySelectorAll('button').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.level === level);
  });
  if (settingsFps) settingsFps.value = window.localStorage.getItem(PRESET_FPS_KEY) ?? String(activePreset.fps);
  if (settingsAntialias) {
    settingsAntialias.value = window.localStorage.getItem(ANTIALIAS_KEY) ?? (activePreset.antialias ? 'on' : 'off');
  }
};
renderGraphicsSettingsUi();

// Derive stored FPS (may have been customised independently of the preset)
const _storedFps = window.localStorage.getItem(PRESET_FPS_KEY);
const _activeFps = _storedFps ? parseInt(_storedFps, 10) : activePreset.fps;
if (settingsFps) settingsFps.value = String(_activeFps);

// Apply preset to localStorage keys consumed downstream (SceneViewportShell reads these)
window.localStorage.setItem(GRAPHICS_QUALITY_KEY, activePreset.quality);
window.localStorage.setItem(ANTIALIAS_KEY, activePreset.antialias ? 'on' : 'off');
if (activePreset.pixelRatioScaleOverride) {
  window.localStorage.setItem(PIXEL_RATIO_SCALE_KEY, String(activePreset.pixelRatioScaleOverride));
} else {
  window.localStorage.removeItem(PIXEL_RATIO_SCALE_KEY);
}

let graphicsQuality: GraphicsQuality = activePreset.quality;
setGraphicsQuality(graphicsQuality);
setUserFpsOverride(_activeFps);

let harpoonCameraView: HarpoonCameraView | null = null;
let bottomHullCameraView: BottomHullCameraView | null = null;
let controllerRenderTuning: RenderTuning = DEFAULT_RENDER_TUNING;

const applyRenderTuning = (tuning: RenderTuning, persistLocal = true): void => {
  controllerRenderTuning = normalizeRenderTuning(tuning);
  if (persistLocal) {
    saveRenderTuningToLocalStorage(controllerRenderTuning);
  }

  harpoonCameraView?.setRenderTuning(controllerRenderTuning);
  bottomHullCameraView?.setRenderTuning(controllerRenderTuning);
  setControllerRenderTuning(controllerRenderTuning);
};

const loadRenderTuning = async (): Promise<void> => {
  const tuning = await loadRenderTuningWithFallback();
  applyRenderTuning(tuning, true);
};

const PERISCOPE_FOCUS_MIN = 2.7;
const PERISCOPE_FOCUS_MAX = 4.5;
const PERISCOPE_FOCUS_STEP = 0.2;
let periscopeFocusValueElement: HTMLElement | null = null;

const getPeriscopeFocus = (): number => {
  return controllerRenderTuning.hullLightFocus;
};

const setPeriscopeFocus = (value: number): void => {
  const nextFocus = clamp(value, PERISCOPE_FOCUS_MIN, PERISCOPE_FOCUS_MAX);
  if (nextFocus === controllerRenderTuning.hullLightFocus) {
    return;
  }

  applyRenderTuning({
    ...controllerRenderTuning,
    hullLightFocus: nextFocus
  });

  if (periscopeFocusValueElement) {
    periscopeFocusValueElement.textContent = `${nextFocus.toFixed(1)}`;
  }
};

const incrementPeriscopeFocus = (): void => {
  setPeriscopeFocus(getPeriscopeFocus() + PERISCOPE_FOCUS_STEP);
};

const decrementPeriscopeFocus = (): void => {
  setPeriscopeFocus(getPeriscopeFocus() - PERISCOPE_FOCUS_STEP);
};

void loadRenderTuning();

/** Dispose all persistent WebGL views. Call when leaving a room or changing rooms. */
const disposeRoomViews = (): void => {
  harpoonCameraView?.dispose();
  harpoonCameraView = null;
  bottomHullCameraView?.dispose();
  bottomHullCameraView = null;
  navigationViewportLayoutCleanup?.();
  navigationViewportLayoutCleanup = null;
};
let navigationViewportLayoutCleanup: (() => void) | null = null;
let pilotDepthGauge: HTMLDivElement | null = null;
let pilotDepthGaugeValue: HTMLElement | null = null;
let pilotDepthGaugeCaption: HTMLParagraphElement | null = null;
let pilotSpeedGauge: HTMLDivElement | null = null;
let pilotSpeedGaugeValue: HTMLElement | null = null;
let pilotSpeedGaugeCaption: HTMLParagraphElement | null = null;
let pilotHeadingGauge: HTMLDivElement | null = null;
let pilotHeadingGaugeValue: HTMLElement | null = null;
let pilotHeadingGaugeCaption: HTMLParagraphElement | null = null;
let captainDepthGauge: HTMLDivElement | null = null;
let captainDepthGaugeValue: HTMLElement | null = null;
let captainDepthGaugeCaption: HTMLParagraphElement | null = null;
let captainSpeedGauge: HTMLDivElement | null = null;
let captainSpeedGaugeValue: HTMLElement | null = null;
let captainSpeedGaugeCaption: HTMLParagraphElement | null = null;
let captainChecklistDepth: HTMLLIElement | null = null;
let captainChecklistWaypoint: HTMLLIElement | null = null;
let captainChecklistHull: HTMLLIElement | null = null;
let captainRoleAssignments: HTMLUListElement | null = null;
let dockingLightsButton: HTMLButtonElement | null = null;
let harpoonLightsButton: HTMLButtonElement | null = null;

let batteryRestoreBtn: HTMLButtonElement | null = null;
let engineResetButton: HTMLButtonElement | null = null;
let engineSlider: HTMLInputElement | null = null;

let dockedToggleButton: HTMLButtonElement | null = null;
let saveGameButton: HTMLButtonElement | null = null;
let dockingViewportElement: HTMLElement | null = null;
let dockingDockedLabelElement: HTMLElement | null = null;
let dockingDistanceLabel: HTMLElement | null = null;
let oxygenGeneratorSlider: HTMLInputElement | null = null;

let activeMap = getMapById(DEFAULT_MAP_ID) ?? {
  id: DEFAULT_MAP_ID,
  name: 'unknown',
  sizeMeters: { width: 200, height: 200 },
  resolution: 1,
  seed: 0,
  baseDepth: 0,
  noiseScale: { a: 0, b: 0, c: 0 },
  mountains: [],
  ridges: [],
  canyons: [],
  minTrenchWidthMeters: 0,
  caverns: [],
  caveArches: [],
  boulderFields: [],
  wrecks: [],
  hydrothermalVents: [],
  seaweedClusters: [],
  currentZones: [],
  researchStations: [],
  missionLayout: {
    submarineSpawn: { x: 0, z: 0, depth: 0, reference: 'surface' },
    waypoint: { x: 0, y: 0, z: 0 }
  }
};
setActiveMapGetter(() => activeMap);
setRoleMetaGetter((role) => ROLE_META[role]);
const prefilledRoomCode = new URLSearchParams(window.location.search).get('code')?.trim().toUpperCase().slice(0, 8) ?? '';
let pendingAutoStart = new URLSearchParams(window.location.search).get('start') === '1';
// True when this controller is the host page's pre-warmed background player
// (embedded in an iframe). It must not take over casting: the parent page owns
// the phone-render stream, and restoring it here would spawn a rival sender.
const isEmbeddedPlayer = (() => { try { return window.self !== window.top; } catch { return true; } })();
// The embedded background player keeps its cast button so the crew can STOP the
// running main screen (or set up a fresh one after stopping) from inside the
// game — previously it had no way to reach the cast panel at all. It still must
// not auto-restore a rival stream: see the `restoreIfActive` guard below.

if (roomInput && prefilledRoomCode) {
  roomInput.value = prefilledRoomCode;
  if (codeConfirmLabel) codeConfirmLabel.textContent = prefilledRoomCode;
  if (lobbyStep1) lobbyStep1.hidden = true;
  if (lobbyStep2) lobbyStep2.hidden = false;
}

type StoredSession = {
  code: string;
  name: string;
  playerId: string;
  reconnectToken: string;
};

const ROLE_META: Record<Role, { label: string; icon: string }> = {
  comms: { label: 'Comms', icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12v8M8 20h8"></path><circle cx="12" cy="10" r="1.6" fill="currentColor" stroke="none"></circle><path d="M8.5 6.5a5 5 0 0 0 0 7M15.5 6.5a5 5 0 0 1 0 7M5.5 4a9 9 0 0 0 0 12M18.5 4a9 9 0 0 1 0 12"></path></svg>' },
  navigation: {
    label: 'Navigation',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" fill="none"></circle><path d="M12 6v2M12 16v2M6 12h2M16 12h2M15 9l-6 3 6 3" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"></path></svg>'
  },
  captain: {
    label: 'Captain',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 4v10l-7 4-7-4V7l7-4Z"></path><path d="M12 8v5"></path><path d="M12 16h.01"></path></svg>'
  },
  docking: {
    label: 'Docking',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 20h12v-4h-4v-8H10v8H6v4Z"></path><path d="M9 12h6"></path></svg>'
  },
  pilot: {
    label: 'Pilot',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="6.25"></circle><path d="M12 2.5v5M12 16.5v5M2.5 12h5M16.5 12h5"></path></svg>'
  },
  ballast: {
    label: 'Ballast',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="5"></circle><path d="M12 7v10M7 12h10"></path></svg>'
  },
  sonar: {
    label: 'Sonar',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="8" cy="12" r="1.8" fill="currentColor" stroke="none"></circle><path d="M10.5 9.5a4.2 4.2 0 0 1 0 5M13 7a7.5 7.5 0 0 1 0 10M16 4.5a11 11 0 0 1 0 15"></path></svg>'
  },
  mining: {
    label: 'MINE',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><polyline points="11 6 5 12 11 18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/><line x1="19" y1="9" x2="19" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
  },
  battery: {
    label: 'Battery',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="7" width="12" height="10" rx="2"></rect><path d="M9 11h6M9 14h4"></path><path d="M12 3v4"></path></svg>'
  },
  storage: {
    label: 'Storage & Processing',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="6" width="14" height="12" rx="2"></rect><path d="M5 10h14M12 10v10"></path></svg>'
  },
  life_support: {
    label: 'Life Support',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5c-1.7 1.6-3 3.8-3 6.2 0 4 3 6.5 3 6.5s3-2.5 3-6.5c0-2.4-1.3-4.6-3-6.2Z"></path><path d="M12 11.5v5"></path></svg>'
  },
  drone: {
    label: 'Drone Control',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12M12 6v12M8 4l-2 2M16 4l2 2M8 20l-2-2M16 20l2-2"></path><circle cx="12" cy="12" r="2"></circle></svg>'
  },

  engineer: {
    label: 'Engineering',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12c0-4.97 4.03-9 9-9s9 4.03 9 9M9 12l6-6M15 12l-6 6M6 9l-3 3h6l-3-3M18 15l3-3h-6l3 3"></path></svg>'
  },
  engine: {
    label: 'Engine',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M8 8h8M8 16h8" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  },
  periscope: {
    label: 'Periscope',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 20v-8a3 3 0 0 1 3-3h3"></path><path d="M15 9V5h4"></path><circle cx="15" cy="15" r="4"></circle></svg>'
  },
  quarters: {
    label: 'Crew Quarters',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 20V5M21 20v-5M3 15h18M3 10h10a3 3 0 0 1 3 3v2"></path><circle cx="7" cy="7.5" r="1.6"></circle></svg>'
  },
  facility_upgrades: {
    label: 'Upgrades',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"></path><path d="M7 8l5-5 5 5"></path><path d="M5 21h14"></path></svg>'
  },
  facility_shop: {
    label: 'Shop',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16l-1.5 12h-13L4 7Z"></path><path d="M9 10V5a3 3 0 0 1 6 0v5"></path></svg>'
  },
  facility_missions: {
    label: 'Missions',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3v18"></path><path d="M6 4h12l-2.5 4L18 12H6"></path></svg>'
  },
  facility_repair: {
    label: 'Repair',
    icon:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 0 5.4-5.4l-2.9 2.9-2.1-2.1 2.9-2.9Z"></path></svg>'
  }
};

const normalizeControllerRoleForUi = (role: Role | null): Role | null => role;

const formatRoleLabel = (role: Role | null): string => (role ? tOpt(`role_${role}`) || ROLE_META[role]?.label || role : t('unassigned'));

const formatDuration = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
};

const SUMMARY_CACHE_KEY = 'aegis7_last_summary';

const cacheSummary = (summary: NonNullable<GameSnapshot['gameSummary']>): void => {
  try { localStorage.setItem(SUMMARY_CACHE_KEY, JSON.stringify(summary)); } catch { /* ignore */ }
};

// True when the summary is the FINAL screen because the game was ended and the
// room closed (no restart possible) — flips the summary's buttons to "Return
// home" and keeps a later stray snapshot from downgrading it.
let summaryTerminal = false;

const showMissionSummary = (summary: NonNullable<GameSnapshot['gameSummary']>, terminal = false): void => {
  if (!controllerMissionSummary) return;
  summaryTerminal = terminal;
  if (controllerSummaryReason) controllerSummaryReason.textContent = summary.reason;
  if (controllerSummaryDuration) controllerSummaryDuration.textContent = formatDuration(summary.durationMs);
  if (controllerSummaryDistance) controllerSummaryDistance.textContent = `${summary.distanceTravelledMeters.toFixed(0)} m`;
  if (controllerSummaryResources) controllerSummaryResources.textContent = `${summary.resourcesCollected}`;
  if (controllerSummarySonar) controllerSummarySonar.textContent = `${summary.sonarPulseCount}`;
  if (controllerSummaryChart) {
    renderMetricChart(controllerSummaryChart, summary.metricHistory ?? []);
  }
  // Restarting needs a live room — hide it once the room has been closed.
  if (controllerSummaryRestartBtn) controllerSummaryRestartBtn.hidden = terminal;
  if (controllerSummaryDismissBtn) controllerSummaryDismissBtn.textContent = terminal ? 'Return Home' : 'Dismiss';
  cacheSummary(summary);
  controllerMissionSummary.hidden = false;
};

const hideMissionSummary = (): void => {
  summaryTerminal = false;
  if (controllerMissionSummary) controllerMissionSummary.hidden = true;
};

controllerSummaryDismissBtn?.addEventListener('click', () => {
  if (summaryTerminal) {
    window.location.href = '/app.html';
    return;
  }
  hideMissionSummary();
});

controllerSummaryRestartBtn?.addEventListener('click', () => {
  const code = controllerState.currentRoomCode;
  if (!code) {
    showControllerQuickToast('Not in a room.');
    return;
  }
  network?.restartGame(code);
  hideMissionSummary();
});

const setStatus = (message: string): void => {
  setTextContent(controllerStatus, message);
};

const setRuntimeStatus = (message: string): void => {
  setTextContent(controllerRuntimeStatus, message);
};

const getHullAudioContext = (): AudioContext | null => {
  const ContextCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!ContextCtor) {
    return null;
  }

  if (!hullAudioContext) {
    hullAudioContext = createMixedAudioContext() as AudioContext;
  }

  if (hullAudioContext.state === 'suspended') {
    void hullAudioContext.resume();
  }

  return hullAudioContext;
};

const createNoiseBuffer = (context: AudioContext, durationSeconds: number): AudioBuffer => {
  const frameCount = Math.max(1, Math.floor(context.sampleRate * durationSeconds));
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frameCount; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

const noiseBufferCache = new Map<string, AudioBuffer>();

const getOrCreateNoiseBuffer = (context: AudioContext, durationSeconds: number): AudioBuffer => {
  const key = `${durationSeconds}`;
  const cached = noiseBufferCache.get(key);
  if (cached) return cached;
  const buf = createNoiseBuffer(context, durationSeconds);
  noiseBufferCache.set(key, buf);
  return buf;
};

const createPilotEngineLoop = (): void => {
  const context = getHullAudioContext();
  if (!context || pilotEngineLoop) {
    return;
  }

  const noiseBuffer = createNoiseBuffer(context, 2);
  const source = context.createBufferSource();
  source.buffer = noiseBuffer;
  source.loop = true;

  const filter = context.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 120;
  filter.Q.value = 1.3;

  const gain = context.createGain();
  gain.gain.setValueAtTime(0, context.currentTime);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(context.destination);
  source.start();

  pilotEngineLoop = {
    source,
    gain,
    filter,
    active: false
  };
};

const stopPilotEngineLoop = (): void => {
  if (!pilotEngineLoop) {
    return;
  }

  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  pilotEngineLoop.active = false;
  const now = context.currentTime;
  pilotEngineLoop.gain.gain.cancelScheduledValues(now);
  pilotEngineLoop.gain.gain.setTargetAtTime(0, now, 0.12);

  if (pilotEngineFadeTimer !== null) {
    window.clearTimeout(pilotEngineFadeTimer);
    pilotEngineFadeTimer = null;
  }

  pilotEngineFadeTimer = window.setTimeout(() => {
    if (pilotEngineLoop && !pilotEngineLoop.active) {
      pilotEngineLoop.source.stop();
      pilotEngineLoop.gain.disconnect();
      pilotEngineLoop.filter.disconnect();
      pilotEngineLoop = null;
    }
    pilotEngineFadeTimer = null;
  }, 220);
};

const updatePilotEngineSound = (submarine: SubmarineState): void => {
  if (controllerState.currentRole !== 'pilot') {
    stopPilotEngineLoop();
    return;
  }

  // Vertical velocity is excluded: diving on ballast is silent, it should not spin the engine up.
  const movement = Math.hypot(submarine.speed, submarine.lateralSpeed);
  const intensity = clamp(movement / 0.75, 0, 1);
  if (intensity < 0.06) {
    stopPilotEngineLoop();
    return;
  }

  createPilotEngineLoop();
  if (!pilotEngineLoop) {
    return;
  }

  const now = getHullAudioContext()?.currentTime;
  if (typeof now !== 'number') {
    return;
  }

  const targetVolume = 0.02 + intensity * 0.08;
  pilotEngineLoop.gain.gain.cancelScheduledValues(now);
  pilotEngineLoop.gain.gain.setTargetAtTime(targetVolume, now, 0.08);
  pilotEngineLoop.filter.frequency.setTargetAtTime(120 + intensity * 200, now, 0.08);
  pilotEngineLoop.active = true;
};

const playWormAttackSound = (strength: number): void => {
  if (!deviceAudioEnabled) return;
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;
  const volume = 0.04 + strength * 0.2;
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  gain.connect(context.destination);

  const noise = context.createBufferSource();
  noise.buffer = getOrCreateNoiseBuffer(context, 0.16);
  const bandpass = context.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = 900 + strength * 500;
  bandpass.Q.value = 6;
  noise.connect(bandpass);
  bandpass.connect(gain);
  noise.start(now);
  noise.stop(now + 0.16);

  const shriek = context.createOscillator();
  shriek.type = 'sine';
  shriek.frequency.setValueAtTime(900 + strength * 600, now);
  shriek.frequency.linearRampToValueAtTime(300 + strength * 300, now + 0.14);

  const shriekGain = context.createGain();
  shriekGain.gain.setValueAtTime(0.0001, now);
  shriekGain.gain.exponentialRampToValueAtTime(volume * 0.55, now + 0.01);
  shriekGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  shriek.connect(shriekGain);
  shriekGain.connect(context.destination);
  shriek.start(now);
  shriek.stop(now + 0.18);
};

const playPowerDownSound = (): void => {
  if (!deviceAudioEnabled) return;
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;

  // Deep descending hum — engine winding down
  const hum = context.createOscillator();
  hum.type = 'sawtooth';
  hum.frequency.setValueAtTime(180, now);
  hum.frequency.exponentialRampToValueAtTime(28, now + 2.4);

  const humGain = context.createGain();
  humGain.gain.setValueAtTime(0.18, now);
  humGain.gain.linearRampToValueAtTime(0.0, now + 2.6);
  hum.connect(humGain);
  humGain.connect(context.destination);
  hum.start(now);
  hum.stop(now + 2.6);

  // Electrical crackle burst at the start
  const crackle = context.createBufferSource();
  crackle.buffer = getOrCreateNoiseBuffer(context, 0.25);
  const crackleFilter = context.createBiquadFilter();
  crackleFilter.type = 'bandpass';
  crackleFilter.frequency.value = 3200;
  crackleFilter.Q.value = 2;
  const crackleGain = context.createGain();
  crackleGain.gain.setValueAtTime(0.22, now);
  crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  crackle.connect(crackleFilter);
  crackleFilter.connect(crackleGain);
  crackleGain.connect(context.destination);
  crackle.start(now);
  crackle.stop(now + 0.25);
};

const getStorageProcessingAudio = (): HTMLAudioElement => {
  if (!storageProcessingAudio) {
    storageProcessingAudio = new Audio('/media/sounds/effects/submarine - servo sound.mp3');
    storageProcessingAudio.loop = true;
    trackEffectsVolume(storageProcessingAudio, 0.22);
    storageProcessingAudio.preload = 'auto';
  }
  return storageProcessingAudio;
};

const updateStorageProcessingLoopState = (roomState: RoomState | null): void => {
  const shouldPlay = controllerState.currentRole === 'storage'
    && !!roomState
    && roomState.craftQueue.length > 0
    && roomState.craftQueue[0].remainingMs > 0;

  if (shouldPlay) {
    const audio = getStorageProcessingAudio();
    if (audio.paused && deviceAudioEnabled) {
      void audio.play().catch(() => {
        // Play may be blocked until user gesture; ignore.
      });
    }
    currentStorageProcessingActive = true;
    return;
  }

  if (storageProcessingAudio && !storageProcessingAudio.paused) {
    storageProcessingAudio.pause();
    storageProcessingAudio.currentTime = 0;
  }
  currentStorageProcessingActive = false;
};

const playRepairClickSound = (): void => {
  if (!deviceAudioEnabled) return;
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.06, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
  gain.connect(context.destination);

  const oscillator = context.createOscillator();
  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(880, now);
  oscillator.frequency.exponentialRampToValueAtTime(620, now + 0.14);
  oscillator.connect(gain);
  oscillator.start(now);
  oscillator.stop(now + 0.14);
};

const playDepthCreakSound = (depthSeverity: number): void => {
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;
  const master = context.createGain();
  const volume = 0.02 + depthSeverity * 0.08;
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(volume, now + 0.06);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);
  master.connect(context.destination);

  const oscillator = context.createOscillator();
  oscillator.type = 'triangle';
  const startHz = 95 - depthSeverity * 34;
  const endHz = 42 - depthSeverity * 14;
  oscillator.frequency.setValueAtTime(startHz, now);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(24, endHz), now + 0.72);

  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(620 - depthSeverity * 340, now);
  filter.Q.value = 1.4;

  oscillator.connect(filter);
  filter.connect(master);
  oscillator.start(now);
  oscillator.stop(now + 0.82);
};

const playCrackSnapSound = (crackSeverity: number): void => {
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;
  const gain = context.createGain();
  const amplitude = 0.05 + crackSeverity * 0.22;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(amplitude, now + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
  gain.connect(context.destination);

  const noiseSource = context.createBufferSource();
  noiseSource.buffer = getOrCreateNoiseBuffer(context, 0.24);
  const highpass = context.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 740;
  noiseSource.connect(highpass);
  highpass.connect(gain);
  noiseSource.start(now);
  noiseSource.stop(now + 0.24);

  const ping = context.createOscillator();
  const pingGain = context.createGain();
  ping.type = 'square';
  ping.frequency.setValueAtTime(620 + crackSeverity * 420, now);
  pingGain.gain.setValueAtTime(0.0001, now);
  pingGain.gain.exponentialRampToValueAtTime(0.08 + crackSeverity * 0.1, now + 0.004);
  pingGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
  ping.connect(pingGain);
  pingGain.connect(context.destination);
  ping.start(now);
  ping.stop(now + 0.11);
};

const playEngineOverloadSiren = (): void => {
  if (!deviceAudioEnabled) return;
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;
  const masterGain = context.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(0.06, now + 0.02);
  masterGain.gain.setValueAtTime(0.06, now + 0.16);
  masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
  masterGain.connect(context.destination);

  const oscillator = context.createOscillator();
  oscillator.type = 'sawtooth';
  oscillator.frequency.setValueAtTime(520, now);
  oscillator.frequency.linearRampToValueAtTime(740, now + 0.14);
  oscillator.frequency.linearRampToValueAtTime(520, now + 0.28);

  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1400, now);
  filter.Q.value = 1.5;

  oscillator.connect(filter);
  filter.connect(masterGain);
  oscillator.start(now);
  oscillator.stop(now + 0.28);
};

const vibratePhone = (pattern: number[]): void => {
  if (vibrationLevel === 'off') return;
  const vibrate = navigator.vibrate?.bind(navigator);
  if (!vibrate) return;
  if (vibrationLevel === 'low') {
    vibrate([pattern[0] ?? 40]);
  } else {
    vibrate(pattern);
  }
};

const triggerEngineOverloadAlert = (): void => {
  vibratePhone([80, 30, 80, 30, 80]);
  playEngineOverloadSiren();
};

const triggerEmpEffects = (): void => {
  flashEmpOverlay();
  vibratePhone([60, 20, 60, 20, 60]);
  playEmpSound();
};

const flashEmpOverlay = (): void => {
  playEmpBlast(controllerEmpFlashOverlay);
};

const playEmpSound = (): void => {
  const context = getHullAudioContext();
  if (!context) {
    return;
  }

  const now = context.currentTime;
  const masterGain = context.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(0.1, now + 0.02);
  masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);
  masterGain.connect(context.destination);

  const noiseSource = context.createBufferSource();
  noiseSource.buffer = getOrCreateNoiseBuffer(context, 0.18);
  const noiseFilter = context.createBiquadFilter();
  noiseFilter.type = 'highpass';
  noiseFilter.frequency.value = 900;
  noiseSource.connect(noiseFilter);
  noiseFilter.connect(masterGain);
  noiseSource.start(now);
  noiseSource.stop(now + 0.18);

  const oscillator = context.createOscillator();
  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(470, now);
  oscillator.frequency.linearRampToValueAtTime(1080, now + 0.16);
  const oscGain = context.createGain();
  oscGain.gain.setValueAtTime(0.0001, now);
  oscGain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  oscillator.connect(oscGain);
  oscGain.connect(masterGain);
  oscillator.start(now);
  oscillator.stop(now + 0.18);
};

/** Phone hiss loudness: faint elsewhere on the boat, loud and urgent when the leak is in the player's own room. */
const LEAK_HISS_ELSEWHERE = 0.03;
const LEAK_HISS_IN_ROOM = 0.2;
const LEAK_BURST_VOLUME_ELSEWHERE = 0.08;
const LEAK_BURST_VOLUME_IN_ROOM = 0.3;
const LEAK_HAPTIC_INTERVAL_MS = 1400;

let leakOverlay: HullLeakOverlay | null = null;
/** Last shared estimate of how late the TV picture is (see castLatency); 0 when no phone-renders cast is up. */
let castLatencyMs = 0;
let castLatencyAt = 0;

let previousLeakCount = 0;
let lastLeakHapticAtMs = 0;

const updateHullLeakFeedback = (
  breaches: Partial<Record<HullZoneId, boolean>>,
  hullPercent?: number,
  zoneHealth?: Partial<Record<HullZoneId, number>>
): void => {
  const leakCount = countHullBreaches(breaches);
  const inLeakingRoom = isRoleInBreachedZone(controllerState.currentRole, breaches);

  leakOverlay ??= createHullLeakOverlay(document.body, { bannerText: 'Leak in this room!', fixed: true });
  leakOverlay.update(
    leakCount,
    inLeakingRoom,
    hullPercent,
    controllerState.currentRole ?? 'none',
    leakSprayCount(breaches, zoneHealth, controllerState.currentRole)
  );

  const nowMs = Date.now();
  if (leakCount > previousLeakCount) {
    vibratePhone(inLeakingRoom ? [200, 60, 200, 60, 300] : [90]);
    lastLeakHapticAtMs = nowMs;
  } else if (inLeakingRoom && nowMs - lastLeakHapticAtMs >= LEAK_HAPTIC_INTERVAL_MS) {
    vibratePhone([70, 40, 70]);
    lastLeakHapticAtMs = nowMs;
  }

  if (deviceAudioEnabled) {
    const context = getHullAudioContext();
    if (context) {
      if (leakCount > previousLeakCount) {
        playLeakBurst(context, inLeakingRoom ? LEAK_BURST_VOLUME_IN_ROOM : LEAK_BURST_VOLUME_ELSEWHERE);
      }
      setLeakHissLevel(context, leakCount === 0 ? 0 : (inLeakingRoom ? LEAK_HISS_IN_ROOM : LEAK_HISS_ELSEWHERE));
    }
  }
  previousLeakCount = leakCount;
};

const playHullWarning = (hull: number): void => {
  // Find the lowest threshold that applies
  let lowestThreshold: number | null = null;
  for (const threshold of [75, 50, 25]) {
    if (hull < threshold) {
      lowestThreshold = threshold;
    }
  }

  // Clear all thresholds above current hull level
  for (const threshold of [75, 50, 25]) {
    if (hull >= threshold) {
      playedHullWarnings.delete(threshold);
    }
  }

  // Only play the lowest applicable threshold
  if (lowestThreshold !== null && !playedHullWarnings.has(lowestThreshold)) {
    playedHullWarnings.add(lowestThreshold);
    if (deviceAudioEnabled) {
      const audio = new Audio(`/media/sounds/voice/hull_${lowestThreshold}.mp3`);
      applyEffectsVolume(audio, 0.7);
      audio.addEventListener('ended', () => { audio.src = ''; }, { once: true });
      void audio.play().catch(() => { /* ignore: may require user gesture */ });
    }
  }
};

const updateHullAudio = (depth: number, crackSeverity: number, verticalVelocity?: number): void => {
  const nowMs = Date.now();
  const depthSeverity = clamp((depth - 80) / 120, 0, 1);

  if (depthSeverity > 0) {
    const minGapMs = Math.round(2200 - depthSeverity * 1700);
    if (nowMs - lastCreakAtMs >= minGapMs) {
      playDepthCreakSound(depthSeverity);
      lastCreakAtMs = nowMs;
    }
  }

  if (crackSeverity > previousCrackSeverity + 0.06) {
    playCrackSnapSound(crackSeverity);
  }
  previousCrackSeverity = crackSeverity;
};

document.addEventListener('pointerdown', () => {
  void getHullAudioContext();
}, { passive: true });

const updateHullWaterLevel = (hull: number | null): number => {
  if (hull === null) {
    return 0;
  }
  const clampedHull = clamp(hull, 0, 100);
  // Water level rises as hull decreases
  const level = clamp((62 - clampedHull) / 42, 0, 1);
  waterLevel = level;
  return level;
};

// ── End Tech Tree panel ───────────────────────────────────────────────────

const showReconnectBanner = (message: string): void => {
  if (!reconnectBanner) {
    return;
  }

  reconnectBanner.textContent = message;
  reconnectBanner.hidden = false;
  window.setTimeout(() => {
    reconnectBanner.hidden = true;
  }, 3200);
};

/** Actually rejoin the previously-stored room (after the crew confirms). */
const performStoredRejoin = (storedSession: StoredSession): void => {
  controllerState.autoJoinPending = true;
  autoOpenRoomMenuOnJoin = true;
  controllerState.currentPlayerId = storedSession.playerId;
  controllerState.reconnectToken = storedSession.reconnectToken;
  network?.joinRoom(storedSession.code, storedSession.name);
};

/**
 * Ask before pulling the player back into their previous room. Auto-rejoining on
 * load could trap them in a broken/looping session with no way out, so a page
 * reload / app restart now offers a choice instead of rejoining silently.
 */
const showRejoinPrompt = (storedSession: StoredSession): void => {
  if (!controllerRejoinDialog) {
    // No dialog available (shouldn't happen) — fall back to the old auto-rejoin.
    performStoredRejoin(storedSession);
    return;
  }
  if (controllerRejoinDialogTitle) {
    const who = storedSession.name ? ` as ${storedSession.name}` : '';
    controllerRejoinDialogTitle.textContent = `Rejoin room ${storedSession.code}${who}?`;
  }
  setStatus(`You were in room ${storedSession.code}. Rejoin, or start fresh.`);
  controllerRejoinDialog.removeAttribute('hidden');
};

const attemptAutoJoin = (): void => {
  const storedSession = loadStoredSession();

  // Socket reconnected while we were in a room: resume our seat silently with the same token (no prompt).
  if (storedSession && controllerState.currentRoomCode === storedSession.code && controllerState.reconnectToken) {
    performStoredRejoin(storedSession);
    autoOpenRoomMenuOnJoin = false;
    return;
  }
  const effectiveRoomCode = prefilledRoomCode || (storedSession?.code ?? '');

  if (!effectiveRoomCode) {
    setStatus(prefilledRoomCode ? `Connected. Room ${prefilledRoomCode} loaded from link.` : 'Connected. Enter a room code to join.');
    return;
  }

  if (roomInput) {
    roomInput.value = effectiveRoomCode;
  }

  if (codeConfirmLabel) codeConfirmLabel.textContent = effectiveRoomCode;
  if (lobbyStep1) lobbyStep1.hidden = true;
  if (lobbyStep2) lobbyStep2.hidden = false;

  if (storedSession?.name && nameInput) {
    nameInput.value = storedSession.name;
  }

  if (storedSession && storedSession.code === effectiveRoomCode) {
    // A prefilled code (from a share/host link, or the "join as player" flow) is
    // an explicit intent to enter that room — proceed. A plain reload with only a
    // remembered session asks first, unless the prompt is already up or we're
    // already in the room.
    if (prefilledRoomCode) {
      performStoredRejoin(storedSession);
      return;
    }
    if (controllerRejoinDialog?.hasAttribute('hidden') !== false && !controllerState.currentRoomCode) {
      showRejoinPrompt(storedSession);
    }
    return;
  }

  const nameValue = nameInput?.value.trim() ?? '';
  if (!nameValue) {
    setStatus(`Connected. Room ${effectiveRoomCode} loaded from link. Enter your name to join.`);
    nameInput?.focus();
    return;
  }
  controllerState.autoJoinPending = true;
  autoOpenRoomMenuOnJoin = true;
  network?.joinRoom(effectiveRoomCode, nameValue);
};

controllerRejoinConfirm?.addEventListener('click', () => {
  controllerRejoinDialog?.setAttribute('hidden', '');
  const storedSession = loadStoredSession();
  if (storedSession) {
    performStoredRejoin(storedSession);
  } else {
    setStatus('Previous session expired — enter a room code to join.');
  }
});

controllerRejoinDecline?.addEventListener('click', () => {
  controllerRejoinDialog?.setAttribute('hidden', '');
  // Forget the remembered session so a later reconnect doesn't ask again, and
  // reset the lobby to a clean "enter a room code" state.
  clearStoredSession();
  controllerState.reconnectToken = '';
  controllerState.currentPlayerId = '';
  if (nameInput) nameInput.value = '';
  if (roomInput) roomInput.value = '';
  if (lobbyStep2) lobbyStep2.hidden = true;
  if (lobbyStep1) lobbyStep1.hidden = false;
  setStatus('Enter a room code to join.');
  roomInput?.focus();
});

let lastScreenStateKey = '';
const updateScreenState = (): void => {
  const showMissionScreen = Boolean(controllerState.currentRoomCode) && controllerState.currentPhase === 'running';
  const hasJoinedRoom = Boolean(controllerState.currentRoomCode);
  const stateKey = `${showMissionScreen}:${hasJoinedRoom}:${controllerState.currentRole ?? ''}`;
  if (stateKey === lastScreenStateKey) return;
  lastScreenStateKey = stateKey;

  if (hasJoinedRoom && !showMissionScreen) {
    // In room lobby — show step 3 (waiting), hide steps 1 & 2
    if (lobbyStep1) lobbyStep1.hidden = true;
    if (lobbyStep2) lobbyStep2.hidden = true;
    if (lobbyStep3) lobbyStep3.hidden = false;
    if (waitingRoomCode) waitingRoomCode.textContent = controllerState.currentRoomCode;
    const roleLabel = controllerState.currentRole ? formatRoleLabel(controllerState.currentRole) : 'Unassigned';
    if (waitingRoleDisplay) waitingRoleDisplay.textContent = roleLabel;
  } else if (!hasJoinedRoom && !showMissionScreen) {
    // Not in any room — reset to step 1, but only if the player isn't mid-way through step 2
    const onStep2 = lobbyStep2 && !lobbyStep2.hidden;
    if (!onStep2) {
      if (lobbyStep1) lobbyStep1.hidden = false;
      if (lobbyStep2) lobbyStep2.hidden = true;
      if (lobbyStep3) lobbyStep3.hidden = true;
    }
  }

  if (controllerLobbyScreen) {
    controllerLobbyScreen.hidden = showMissionScreen;
    controllerLobbyScreen.classList.toggle('controller-screen-active', !showMissionScreen);
  }

  if (controllerMissionScreen) {
    controllerMissionScreen.hidden = !showMissionScreen;
    controllerMissionScreen.classList.toggle('controller-screen-active', showMissionScreen);
  }
};

const updateControllerMeta = (): void => {
  if (assignedRole) {
    assignedRole.textContent = controllerState.currentRole ? `${t('assigned')}: ${formatRoleLabel(controllerState.currentRole)}` : t('unassigned');
  }

  if (lobbyRoomMeta) {
    lobbyRoomMeta.textContent = controllerState.currentRoomCode || t('no_room_joined');
  }

  if (roomMapCloseButton) {
    roomMapCloseButton.disabled = !Boolean(controllerState.currentRoomCode);
  }
};

const updateRoomHeader = (roomState: RoomState | null): void => {
  if (!roomMenuButton || !ctrlRoomHealthFill || !ctrlRoomHealthValue) {
    return;
  }

  const role = controllerState.currentRole;
  const roomCode = controllerState.currentRoomCode;
  const roleLabel = role ? (tOpt(`role_${role}`) || (ROLE_META[role]?.label ?? role)) : null;
  const roomLabel = roleLabel ? roleLabel.toUpperCase() : roomCode ? roomCode.toUpperCase() : 'Rooms';
  roomMenuButton.textContent = roomLabel;

  if (!roomState || !role) {
    ctrlRoomHealthFill.style.width = '0%';
    ctrlRoomHealthFill.style.backgroundColor = 'var(--ctrl-bar-fill-crit)';
    ctrlRoomHealthValue.textContent = '--%';
    return;
  }

  const roomHealthValue = Math.max(0, Math.min(100, roomState.roomHealth[role] ?? 0));
  ctrlRoomHealthFill.style.width = `${roomHealthValue}%`;
  ctrlRoomHealthFill.style.backgroundColor = roomHealthValue > 65
    ? 'var(--ctrl-bar-fill-ok)'
    : roomHealthValue > 35
      ? 'var(--ctrl-bar-fill-warn)'
      : 'var(--ctrl-bar-fill-crit)';
  ctrlRoomHealthValue.textContent = `${Math.round(roomHealthValue)}%`;
};

const createZoneReadout = (
  label: string,
  value: string,
  variant: ReadoutVariant = 'amber',
  unit?: string
): HTMLDivElement => {
  const row = document.createElement('div');
  row.className = 'ctrl-readout-row';

  const labelWrap = document.createElement('div');
  labelWrap.className = 'ctrl-readout-row-label-wrap';

  const labelEl = document.createElement('span');
  labelEl.className = 'ctrl-readout-row-label';
  labelEl.textContent = label;

  labelWrap.append(labelEl);
  if (unit) {
    const unitEl = document.createElement('span');
    unitEl.className = 'ctrl-readout-row-unit';
    unitEl.textContent = unit;
    labelWrap.append(unitEl);
  }

  const valueEl = document.createElement('span');
  valueEl.className = `ctrl-seg-display${variant === 'amber' ? '' : ` ${variant}`}`;
  valueEl.textContent = value;

  row.append(labelWrap, valueEl);
  return row;
};

const clearZone = (zone: HTMLElement | null): void => {
  if (zone) {
    zone.innerHTML = '';
  }
};

const bindPressAction = (button: HTMLButtonElement, action: () => void): void => {
  let suppressNextClick = false;

  if ('PointerEvent' in window) {
    button.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) {
        return;
      }

      event.preventDefault();
      suppressNextClick = true;
      action();
    });
  }

  button.addEventListener('click', (event) => {
    if (suppressNextClick) {
      suppressNextClick = false;
      event.preventDefault();
      return;
    }

    action();
  });
};

// Roles whose lower-left / lower-right zones are built once on entry (not rebuilt every snapshot):
// without this the per-snapshot readout pass wiped the Harpoon's lights/fire controls right after they were created.
const preserveLeftBottomRoles: Set<Role> = new Set([
  'battery',
  'ballast',
  'captain',
  'engine',
  'docking',
  'drone',
  'mining',
  'life_support',
  'pilot',
  'storage',
  'sonar'
]);

const preserveRightAuxRoles: Set<Role> = new Set([
  'ballast',
  'battery',
  'captain',
  'drone',
  'engine',
  'mining',
  'life_support',
  'navigation',
  'pilot',
  'storage',
  'docking',
  'periscope',
  'sonar'
]);

const zoneReadoutCache = new Map<string, HTMLElement>();
let lastZoneRole: string | null = null;
let sonarModeDescEl: HTMLElement | null = null;

const SONAR_MODE_DESCRIPTIONS: Record<string, string> = {
  terrain: 'Maps nearest surface only. Does not update nav map.',
  resource: 'Shows resource clouds in range. Updates nav map.',
  subsurface: 'Reveals full terrain incl. caves. Updates nav map.'
};

const updateMissionZoneReadouts = (roomState: RoomState | null): void => {
  if (!ctrlZoneLeftTop || !ctrlZoneLeftBottom) {
    return;
  }

  const role = normalizeControllerRoleForUi(controllerState.currentRole);
  const snapshot = controllerState.lastSnapshot;
  if (!role) {
    // Role vacated (walking between rooms / evacuated): renderControls wiped
    // the zone DOM, so cached readout spans are dead. Reset the role tracker
    // so re-entering a room — even the same one — rebuilds instead of
    // updating detached elements.
    lastZoneRole = null;
    clearHarpoonUiElements();
    return;
  }
  if (!snapshot || !roomState) {
    clearHarpoonUiElements();
    return;
  }

  // The harpoon's fire/scan buttons and reload bar are bound once when the room is entered. Dropping the
  // references on every snapshot (as this used to) froze them: the button never flipped to Retrieve /
  // Reloading and the reload bar never moved. Only release them once the harpoon station is left.
  if (role !== 'mining') {
    clearHarpoonUiElements();
  }

  const roleChanged = role !== lastZoneRole;
  lastZoneRole = role;

  // Helper: create a readout element and cache its value span, or update the cached span in place.
  const zoneReadout = (label: string, value: string, variant: ReadoutVariant = 'amber', unit?: string): HTMLDivElement | null => {
    const cacheKey = `leftTop:${label}`;
    if (!roleChanged) {
      const cached = zoneReadoutCache.get(cacheKey);
      if (cached) {
        cached.textContent = value;
        // Update variant class
        cached.className = `ctrl-seg-display${variant === 'amber' ? '' : ` ${variant}`}`;
        return null; // no need to append
      }
    }
    const el = createZoneReadout(label, value, variant, unit);
    const valueEl = el.querySelector('.ctrl-seg-display') as HTMLElement | null;
    if (valueEl) {
      zoneReadoutCache.set(cacheKey, valueEl);
    }
    return el;
  };

  // Helper: append zone readouts, skipping nulls (in-place update case).
  const appendReadouts = (zone: HTMLElement, ...els: (HTMLDivElement | null)[]): void => {
    for (const el of els) {
      if (el) zone.append(el);
    }
  };

  if (roleChanged) {
    // Clear cached value spans for leftTop on role change.
    for (const key of [...zoneReadoutCache.keys()]) {
      if (key.startsWith('leftTop:')) zoneReadoutCache.delete(key);
    }
    ctrlZoneLeftTop.innerHTML = '';
  }

  const preserveLeftBottom = preserveLeftBottomRoles.has(role);
  const preserveRightAux = preserveRightAuxRoles.has(role);

  if (!preserveLeftBottom) {
    clearZone(ctrlZoneLeftBottom);
  }
  if (ctrlZoneRightAux && !preserveRightAux) {
    clearZone(ctrlZoneRightAux);
  }

  const submarine = snapshot.submarine;
  const power = Math.round(submarine.batteryCharge ?? 0);
  const speed = Math.hypot(submarine.speed, submarine.lateralSpeed, submarine.verticalVelocity);
  const heading = normalizeHeadingDegrees(submarine.yaw);

  if (role === 'pilot') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Depth', `${Math.round(submarine.depth)}`, 'amber', 'metres'),
      zoneReadout('HDG', `${Math.round(heading)}`, 'amber', 'degrees'),
      zoneReadout('Speed', `${speed.toFixed(2)}`, 'cyan', 'metres/sec'),
      zoneReadout('Fuel', `${Math.round(submarine.fuel)}`, resourceReadoutVariant('fuel', submarine.fuel), 'percent'),
      zoneReadout('Power', `${power}`, resourceReadoutVariant('battery', power), 'megawatt')
    );
    return;
  }

  if (role === 'engine') {
    const netMw = (submarine.powerSupplyMw ?? 0) - (submarine.powerDemandMw ?? 0);
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Fuel', `${Math.round(submarine.fuel)}`, resourceReadoutVariant('fuel', submarine.fuel), 'percent'),
      zoneReadout('Net', `${netMw >= 0 ? '+' : ''}${netMw.toFixed(1)}`, netMw >= 0 ? 'amber' : 'red', 'megawatt'),
      zoneReadout('Output', `${Math.round(submarine.engineOutput)}`, 'amber', 'megawatt')
    );
    return;
  }

  if (role === 'ballast') {
    // Depth/rate readouts in the top-left strip.
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Depth', `${Math.round(submarine.depth)}`, 'amber', 'metres'),
      zoneReadout('V-Rate', `${submarine.verticalVelocity.toFixed(2)}`, 'cyan', 'metres/sec')
    );
    // Gauge cards and DIVE/RISE buttons are rendered by BallastRoomController
    // directly into ctrlZoneLeftBottom (fore) and ctrlZoneRightAux (aft).
    // The trim silhouette goes into the centre #controls zone via renderBallastControls().
    return;
  }

  if (role === 'sonar') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Power', `${(getSonarPulsePowerMw(getSonarRangeMeters()) * getSonarScanCostMultiplier(getSonarScanType())).toFixed(1)}`, 'amber', 'megawatt'),
      zoneReadout('Range', `${Math.round(getSonarRangeMeters())}`, 'cyan', 'metres'),
      zoneReadout('Battery', `${Math.round(submarine.batteryCharge)}`, 'amber', 'percent')
    );

    if (roleChanged) {
      sonarModeDescEl = document.createElement('p');
      sonarModeDescEl.className = 'sonar-mode-desc';
      ctrlZoneLeftTop.append(sonarModeDescEl);
    }
    if (sonarModeDescEl) {
      sonarModeDescEl.textContent = SONAR_MODE_DESCRIPTIONS[getSonarScanType()] ?? '';
    }

    if (roleChanged) {
      // Add sonar range controls to left bottom zone
      const sonarRangeControl = createPlusMinusControl(
        'Sonar range',
        {
          title: 'Decrease sonar range',
          ariaLabel: 'Decrease sonar range',
          onStep: decrementSonarRange
        },
        {
          title: 'Increase sonar range',
          ariaLabel: 'Increase sonar range',
          onStep: incrementSonarRange
        },
        { buttonRowClassName: 'sonar-zone-range-controls' }
      );
      ctrlZoneLeftBottom.append(sonarRangeControl.wrap);

      // Add ping button to right aux zone
      const pingButton = document.createElement('button');
      pingButton.type = 'button';
      pingButton.className = 'primary sonar-zone-ping-button';
      pingButton.textContent = 'PING';
      pingButton.title = 'Emit sonar pulse';
      bindPressAction(pingButton, () => {
        const scanType = getSonarScanType();
        emitControl({ action: 'sonar_ping', value: Math.round(getSonarRangeMeters()), scanType });
        // The chart reveal runs when the server's sonar_pulse event arrives (onSonarPulse), so a ping the server rejects (no battery) reveals nothing.
        if (controllerState.lastSnapshot) captureSonarPingHitsForNavigation(controllerState.lastSnapshot, scanType);
      });

      if (ctrlZoneRightAux) {
        ctrlZoneRightAux.innerHTML = '';
        ctrlZoneRightAux.append(pingButton);
      }
    }

    return;
  }

  if (role === 'periscope') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('HDG', `${Math.round(heading)}`, 'cyan', 'degrees'),
      zoneReadout('Speed', `${speed.toFixed(2)}`, 'cyan', 'metres/sec'),
      zoneReadout('Depth', `${Math.round(submarine.depth)}`, 'amber', 'metres')
    );

    // Add focus controls to left bottom zone (requires unlock)
    if (ctrlZoneLeftBottom && controllerState.lastRoomState?.ownedUpgrades.includes('unlock-periscope-focus')) {
      const focusReadout = createZoneReadout('Spot focus', `${getPeriscopeFocus().toFixed(1)}`, 'cyan');
      periscopeFocusValueElement = focusReadout.querySelector('.ctrl-seg-display');

      const focusControl = createPlusMinusControl(
        'Spot focus',
        {
          title: 'Widen focus',
          ariaLabel: 'Widen focus',
          onStep: decrementPeriscopeFocus
        },
        {
          title: 'Narrow focus',
          ariaLabel: 'Narrow focus',
          onStep: incrementPeriscopeFocus
        },
        { buttonRowClassName: 'periscope-zone-focus-controls' }
      );
      ctrlZoneLeftBottom.append(focusReadout, focusControl.wrap);
    }

    // Add lights button to right aux zone — only once (zone is preserved across snapshots)
    if (ctrlZoneRightAux && ctrlZoneRightAux.childElementCount === 0) {
      const lightsButton = createPeriscopeLightsButton();
      lightsButton.className = 'primary periscope-zone-lights-button';
      ctrlZoneRightAux.append(lightsButton);
    }

    return;
  }

  if (role === 'mining') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Battery', `${Math.round(submarine.batteryCharge)}`, 'amber', 'percent')
    );

    if (roleChanged) {
      const harpoonPowerRequirements = document.createElement('div');
      harpoonPowerRequirements.className = 'hp-power';
      harpoonPowerRequirements.innerHTML = `
        <div class="hp-power-title">Power draw</div>
        <div class="hp-power-row"><span>Fire</span><b>${HARPOON_FIRE_POWER_MW} MW</b></div>
        <div class="hp-power-row"><span>Scan</span><b>${HARPOON_SCAN_POWER_MW} MW</b></div>
        <div class="hp-power-row"><span>Lights</span><b>${LIGHTS_POWER_USAGE_PER_MINUTE} MW</b></div>
      `;
      ctrlZoneLeftTop.append(harpoonPowerRequirements);
    }

    if (roleChanged && ctrlZoneLeftBottom && controllerState.lastRoomState?.ownedUpgrades.includes(MINING_LIGHTS_UPGRADE_ID)) {
      const miningLightsOn = controllerState.lastSnapshot?.submarine.miningLightsOn ?? false;
      const lightsButton = document.createElement('button');
      lightsButton.type = 'button';
      lightsButton.className = 'primary';
      lightsButton.textContent = miningLightsOn ? 'Turn mining lights OFF' : 'Turn mining lights ON';
      bindPressAction(lightsButton, () => {
        if (!controllerState.currentRoomCode || !controllerState.lastSnapshot) {
          return;
        }
        const nextMiningLightsOn = !controllerState.lastSnapshot.submarine.miningLightsOn;
        const predictedSnapshot = {
          ...controllerState.lastSnapshot,
          submarine: {
            ...controllerState.lastSnapshot.submarine,
            miningLightsOn: nextMiningLightsOn
          }
        };
        controllerState.lastSnapshot = predictedSnapshot;
        emitControl({ action: 'toggle_mining_lights', active: nextMiningLightsOn });
        updatePowerAndLightUi(predictedSnapshot);
        lightsButton.textContent = nextMiningLightsOn ? 'Turn mining lights OFF' : 'Turn mining lights ON';
      });
      harpoonLightsButton = lightsButton;
      ctrlZoneLeftBottom.append(lightsButton);
    }

    if (roleChanged && ctrlZoneRightAux) {
      ctrlZoneRightAux.innerHTML = '';
      ctrlZoneRightAux.style.display = 'grid';
      ctrlZoneRightAux.style.gap = '0.5rem';
      ctrlZoneRightAux.style.alignItems = 'stretch';

      const reloadTrack = document.createElement('div');
      reloadTrack.className = 'hp-reload-track';

      const reloadFill = document.createElement('div');
      reloadFill.className = 'hp-reload-fill';
      reloadTrack.append(reloadFill);

      const reloadStatus = document.createElement('div');
      reloadStatus.className = 'hp-reload-status';
      reloadStatus.textContent = 'Harpoon ready.';

      const fireButton = document.createElement('button');
      fireButton.type = 'button';
      fireButton.className = 'primary hp-fire';
      fireButton.textContent = 'FIRE';
      bindPressAction(fireButton, () => {
        tryHarpoonAction();
      });

      let scanButton: HTMLButtonElement | null = null;
      if (controllerState.lastRoomState?.ownedUpgrades.includes(MINING_SCAN_UPGRADE_ID)) {
        scanButton = document.createElement('button');
        scanButton.type = 'button';
        scanButton.className = 'primary';
        scanButton.textContent = 'SCAN';
        bindPressAction(scanButton, () => {
          tryHarpoonScan();
        });
      }

      ctrlZoneRightAux.append(reloadTrack, reloadStatus, fireButton, ...(scanButton ? [scanButton] : []));
      bindHarpoonUiElements(fireButton, scanButton, reloadFill, reloadStatus);
    }
    return;
  }

  if (role === 'captain') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Depth', `${Math.round(submarine.depth)}`, 'amber', 'metres'),
      zoneReadout('Speed', `${speed.toFixed(2)}`, 'cyan', 'metres/sec'),
      zoneReadout('Hull', `${Math.round(submarine.hull)}`, submarine.hull < 40 ? 'red' : 'amber', 'percent')
    );
    return;
  }

  if (role === 'engineer') {
    // Damage-control picture: how sealed the hull is, how many leaks, how many rooms are taking water.
    const leaks = Object.values(submarine.hullBreaches).filter(Boolean).length;
    const wetRooms = Object.values(submarine.roomFlood ?? {}).filter((level) => (level ?? 0) >= 1).length;
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Hull', `${Math.round(submarine.hull)}`, resourceReadoutVariant('hull', submarine.hull), 'percent'),
      zoneReadout('Leaks', `${leaks}`, leaks > 0 ? 'red' : 'cyan', 'zones'),
      zoneReadout('Flooded', `${wetRooms}`, wetRooms > 0 ? 'yellow' : 'cyan', 'rooms')
    );
    return;
  }

  if (role === 'battery') {
    const batteryCharge = Math.round(submarine.batteryCharge);
    const demandMw = submarine.powerDemandMw ?? 0;
    const netMw = (submarine.powerSupplyMw ?? 0) - demandMw;
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Battery', `${batteryCharge}`, resourceReadoutVariant('battery', batteryCharge), 'percent'),
      zoneReadout('Demand', `${demandMw.toFixed(1)}`, 'cyan', 'megawatt'),
      zoneReadout('Net', `${netMw >= 0 ? '+' : ''}${netMw.toFixed(1)}`, netMw >= 0 ? 'amber' : 'red', 'megawatt')
    );
    return;
  }

  if (role === 'life_support') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('O2', `${Math.round(submarine.oxygen)}`, resourceReadoutVariant('oxygen', submarine.oxygen, 'cyan'), 'percent'),
      zoneReadout('Gen', `${Math.round(submarine.oxygenGeneratorLevel)}`, 'amber', 'percent'),
      zoneReadout('Docked', submarine.docked ? 'YES' : 'NO', submarine.docked ? 'cyan' : 'amber', 'status')
    );
    return;
  }

  if (role === 'drone') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Depth', `${Math.round(submarine.depth)}`, 'amber', 'metres')
    );
    return;
  }

  if (role === 'storage') {
    const storedUnits = Math.round(getStorageSpotsUsed(roomState.cargo) ?? Object.values(roomState.cargo).reduce((sum, qty) => sum + qty, 0));
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Cargo', `${storedUnits}`, 'amber', `of ${STORAGE_CAPACITY} spots`),
      zoneReadout('Queue', `${roomState.craftQueue.length}`, 'amber', 'jobs'),
      zoneReadout('Power', `${power}`, 'amber', 'megawatt')
    );
    return;
  }

  if (role === 'navigation' || role === 'docking') {
    appendReadouts(ctrlZoneLeftTop,
      zoneReadout('Depth', `${Math.round(submarine.depth)}`, 'amber', 'metres'),
      zoneReadout('HDG', `${Math.round(heading)}`, 'amber', 'degrees'),
      zoneReadout('Speed', `${speed.toFixed(2)}`, 'cyan', 'metres/sec')
    );
    if (role === 'navigation') {
      appendReadouts(ctrlZoneLeftTop,
        zoneReadout('Battery', `${Math.round(submarine.batteryCharge)}`, resourceReadoutVariant('battery', submarine.batteryCharge), 'percent')
      );
      if (roleChanged) {
        const note = document.createElement('p');
        note.className = 'nav-scan-note';
        note.textContent = `Sub-surface scan: max range only, costs ${Math.round(SUBSURFACE_SCAN_COST)}% battery. Reveals caves and tunnels.`;
        ctrlZoneLeftTop.append(note);
      }
    }
  }
};

const updateRoomRepairButton = (roomState: RoomState): void => {
  if (!controllerRoomRepairButton) {
    return;
  }

  const canRepair = Boolean(controllerState.currentRole && roomState.phase === 'running' && controllerState.currentRoomCode);
  controllerRoomRepairButton.hidden = !canRepair;
  controllerRoomRepairButton.disabled = !canRepair;
  controllerRoomRepairButton.title = canRepair
    ? 'Repair this room'
    : 'Room repairs are available once the mission starts.';
  controllerRoomRepairButton.classList.toggle('active', canRepair);
  controllerRoomRepairButton.classList.toggle('disabled', !canRepair);
};

controllerRoomRepairButton?.addEventListener('click', () => {
  if (!controllerState.currentRole || controllerState.currentPhase !== 'running' || !controllerState.currentRoomCode) {
    return;
  }

  network?.engineerAction({
    code: controllerState.currentRoomCode,
    action: 'repair_room',
    room: controllerState.currentRole
  });
  playRepairClickSound();
});

const updateSettingsMeta = (): void => {
  renderGraphicsSettingsUi();
  if (settingsVibration) {
    settingsVibration.value = vibrationLevel;
  }
  if (settingsDeviceAudio) {
    settingsDeviceAudio.value = deviceAudioEnabled ? 'on' : 'off';
  }
  syncAudioSliders();
};

/** Show the TV's current volumes (from the room), or the defaults before anyone has set them. */
const getTvAudioLevels = (): { music: number; effects: number } =>
  controllerState.lastRoomState?.castAudio ?? { music: DEFAULT_MIX.music, effects: DEFAULT_MIX.effects };

const syncAudioSliders = (): void => {
  const inRoom = Boolean(controllerState.currentRoomCode);
  const tv = getTvAudioLevels();
  if (settingsTvMusic) {
    settingsTvMusic.value = String(Math.round(tv.music * 100));
    settingsTvMusic.disabled = !inRoom;
  }
  if (settingsTvEffects) {
    settingsTvEffects.value = String(Math.round(tv.effects * 100));
    settingsTvEffects.disabled = !inRoom;
  }
  if (settingsPhoneVolume) settingsPhoneVolume.value = String(Math.round(getMixVolume('effects') * 100));
  const hint = document.getElementById('settingsAudioHint');
  if (hint) hint.textContent = inRoom ? t('settings_audio_hint') : t('settings_audio_need_room');
};

const toggleSettingsPanel = (open: boolean): void => {
  if (!controllerSettingsPanel) {
    return;
  }

  if (open) {
    updateSettingsMeta();
    // Update translated labels
    const settingsPanelTitle = document.getElementById('settingsPanelTitle');
    const settingsClose = document.getElementById('controllerSettingsClose');
    const labelGraphics = document.getElementById('settingsLabelGraphics');
    const labelVibration = document.getElementById('settingsLabelVibration');
    const labelAudio = document.getElementById('settingsLabelAudio');
    const labelLanguage = document.getElementById('settingsLabelLanguage');
    const labelAudioSection = document.getElementById('settingsLabelAudioSection');
    const labelTvMusic = document.getElementById('settingsLabelTvMusic');
    const labelTvEffects = document.getElementById('settingsLabelTvEffects');
    const labelPhoneVolume = document.getElementById('settingsLabelPhoneVolume');
    if (labelAudioSection) labelAudioSection.textContent = t('settings_audio_section');
    if (labelTvMusic) labelTvMusic.textContent = t('settings_tv_music');
    if (labelTvEffects) labelTvEffects.textContent = t('settings_tv_effects');
    if (labelPhoneVolume) labelPhoneVolume.textContent = t('settings_phone_volume');
    if (settingsPanelTitle) settingsPanelTitle.textContent = t('settings_title');
    if (settingsClose) settingsClose.textContent = t('close');
    if (labelGraphics) labelGraphics.textContent = t('settings_graphics');
    if (labelVibration) labelVibration.textContent = t('settings_vibration');
    if (labelAudio) labelAudio.textContent = t('settings_audio');
    if (labelLanguage) labelLanguage.textContent = t('settings_language');
    if (settingsLanguage) settingsLanguage.value = getLanguage();
  }

  setHidden(controllerSettingsPanel, !open);
};

const toggleRoomHelpPanel = (open: boolean): void => {
  if (!controllerRoomHelpPanel) {
    return;
  }

  if (open) {
    const role = controllerState.currentRole;
    if (controllerRoomHelpTitle) {
      controllerRoomHelpTitle.textContent = role ? (tOpt(`role_${role}`) || (ROLE_META[role]?.label ?? t('room_help'))) : t('room_help');
    }
    if (controllerRoomHelpDescription) {
      const text = role
        ? (tOpt(`help_${role}`) || (ROOM_HELP_TEXT[role] ?? t('room_help_default')))
        : t('room_help_select');
      controllerRoomHelpDescription.innerHTML = '';
      for (const para of text.split('\n\n')) {
        const p = document.createElement('p');
        p.textContent = para.trim();
        controllerRoomHelpDescription.append(p);
      }
    }
  }

  setHidden(controllerRoomHelpPanel, !open);
};

updateScreenState();

const quantizePilotEngineThrottle = (value: number): number => {
  const clampedValue = clamp(value, -1, 1);
  return Math.round(clampedValue * PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION) / PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION;
};

const updateCaptainPanel = (snapshot: GameSnapshot | null): void => {
  // Drive the modular captain console (tabbed view). The legacy gauge code below is inert
  // (its element refs are never assigned) but is retained to avoid unused-variable churn.
  updateCaptainConsole(snapshot);
  updateCaptainRoleAssignments();

  if (!snapshot) {
    return;
  }

  if (captainChecklistDepth) {
    const targetDepth = 10;
    const complete = snapshot.submarine.depth >= targetDepth;
    captainChecklistDepth.textContent = `${complete ? 'Done' : 'Pending'} | Reach depth ${targetDepth}m`;
    captainChecklistDepth.classList.toggle('complete', complete);
  }

  if (captainChecklistWaypoint) {
    const waypointWorld = salvageTargetToMapPoint(snapshot.world.salvageTarget);
    const submarineWorld = submarineToMapPoint(snapshot.submarine);
    const distanceToWaypoint = Math.hypot(waypointWorld.x - submarineWorld.x, waypointWorld.z - submarineWorld.z);
    const complete = distanceToWaypoint <= 15;
    captainChecklistWaypoint.textContent = `${complete ? 'Done' : 'Pending'} | Close to waypoint (<=15m)`;
    captainChecklistWaypoint.classList.toggle('complete', complete);
  }

  if (captainChecklistHull) {
    const complete = snapshot.submarine.hull >= 70;
    captainChecklistHull.textContent = `${complete ? 'Done' : 'Pending'} | Keep hull >= 70%`;
    captainChecklistHull.classList.toggle('complete', complete);
  }

  if (captainDepthGauge && captainDepthGaugeValue && captainDepthGaugeCaption) {
    const displayDepth = snapshot.submarine.depth + ((activeMap?.missionLayout?.submarineSpawn as any)?.startingDepthOffset ?? 0);
    const depthMax = 80;
    const depthFill = clamp(displayDepth / depthMax, 0, 1);
    captainDepthGauge.style.setProperty('--captain-gauge-fill', `${depthFill * 270}deg`);
    captainDepthGaugeValue.textContent = `${displayDepth.toFixed(1)}m`;
    captainDepthGaugeCaption.textContent = `Target 10m+`;
  }

  if (captainSpeedGauge && captainSpeedGaugeValue && captainSpeedGaugeCaption) {
    const speed = Math.hypot(snapshot.submarine.speed, snapshot.submarine.lateralSpeed, snapshot.submarine.verticalVelocity);
    const speedMax = 2;
    const speedFill = clamp(speed / speedMax, 0, 1);
    captainSpeedGauge.style.setProperty('--captain-gauge-fill', `${speedFill * 270}deg`);
    captainSpeedGaugeValue.textContent = `${speed.toFixed(2)}m/s`;
    captainSpeedGaugeCaption.textContent = `Vector speed`;
  }
};

let lastCaptainPlayersJson = '';

const updateCaptainRoleAssignments = (): void => {
  if (!captainRoleAssignments) {
    return;
  }

  const players = controllerState.lastRoomState?.players ?? [];
  const sortedPlayers = [...players].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const json = JSON.stringify(sortedPlayers.map(p => ({ name: p.name, role: p.role })));
  if (json === lastCaptainPlayersJson) return;
  lastCaptainPlayersJson = json;

  captainRoleAssignments.innerHTML = '';

  for (const player of sortedPlayers) {
    const row = document.createElement('li');
    row.className = 'captain-role-assignment-row';

    const playerName = document.createElement('span');
    playerName.className = 'captain-role-assignment-label';
    playerName.textContent = player.name || 'Unknown crew member';

    const assignedRoom = document.createElement('strong');
    assignedRoom.className = 'captain-role-assignment-value';
    assignedRoom.textContent = player.role ? (tOpt(`role_${player.role}`) || (ROLE_META[player.role]?.label ?? player.role)) : t('no_room');

    row.append(playerName, assignedRoom);
    captainRoleAssignments.append(row);
  }
};

const emitControl = (input: ControlInput): void => {
  network?.emitControl(input);
};

const getNearestDockingPortDistanceMeters = (snapshot: GameSnapshot): number => {
  if (!activeMap) {
    return Infinity;
  }

  const origin = getDockingCameraOriginMapPoint(snapshot);
  let closestDistance = Infinity;

  const markers = (activeMap as any).glbModelMarkers ?? [];
  for (const marker of markers) {
    if (marker.model.toLowerCase() !== 'docking_port') {
      continue;
    }

    const distance = getDockingDistanceMeters(marker, markers, origin.x, origin.z, origin.y);
    if (distance < closestDistance) {
      closestDistance = distance;
    }
  }

  return closestDistance;
};

const isDockingInRange = (snapshot: GameSnapshot): boolean => {
  return getNearestDockingPortDistanceMeters(snapshot) <= DOCKING_ACTIVATION_RANGE_METERS;
};

const updateDockingViewportDockedState = (isDocked: boolean): void => {
  if (dockingDockedLabelElement) {
    dockingDockedLabelElement.style.display = isDocked ? 'flex' : 'none';
  }

  if (!dockingViewportElement) {
    return;
  }

  const cameraCanvas = dockingViewportElement.querySelector('canvas');
  if (cameraCanvas instanceof HTMLElement) {
    cameraCanvas.style.visibility = isDocked ? 'hidden' : 'visible';
  }
};

const updateDockingSaveButtonState = (snapshot: GameSnapshot | null): void => {
  if (!saveGameButton) {
    return;
  }

  const isDocked = snapshot?.submarine.docked ?? false;
  saveGameButton.disabled = !isDocked;
  saveGameButton.title = isDocked
    ? 'Save the current game while docked.'
    : 'Save is only available while submarine is docked.';
};

const renderDockingControls = (): void => {
  if (!controls) {
    return;
  }
  controls.classList.add('camera-role-layout');

  const dockingCard = createCard('Docking camera', 'Downward-facing camera for docking operations with converging lights focused 5m below the hull.');
  dockingCard.classList.add('ctrl-room-main-card');
  const dockingViewport = document.createElement('div');
  dockingViewport.className = 'docking-hull-camera-viewport role-camera-viewport';
  dockingViewportElement = dockingViewport;

  const dockedLabel = document.createElement('div');
  dockedLabel.className = 'muted';
  dockedLabel.textContent = 'docked';
  dockedLabel.style.position = 'absolute';
  dockedLabel.style.inset = '0';
  dockedLabel.style.display = 'none';
  dockedLabel.style.alignItems = 'center';
  dockedLabel.style.justifyContent = 'center';
  dockedLabel.style.fontSize = '1rem';
  dockedLabel.style.fontWeight = '600';
  dockedLabel.style.letterSpacing = '0.08em';
  dockedLabel.style.textTransform = 'uppercase';
  dockedLabel.style.pointerEvents = 'none';
  dockedLabel.style.background = 'rgba(4, 12, 24, 0.65)';
  dockedLabel.style.zIndex = '2';
  dockingDockedLabelElement = dockedLabel;
  dockingViewport.append(dockedLabel);

  const dockingToggle = document.createElement('button');
  dockingToggle.type = 'button';
  dockingToggle.className = 'primary';
  dockingToggle.textContent = controllerState.lastSnapshot?.submarine.dockingLightsOn ? 'Turn docking lights OFF' : 'Turn docking lights ON';
  dockingLightsButton = dockingToggle;
  dockingToggle.addEventListener('click', () => {
    if (!controllerState.currentRoomCode || !controllerState.lastSnapshot) {
      return;
    }
    emitControl({
      action: 'toggle_docking_lights',
      active: !controllerState.lastSnapshot.submarine.dockingLightsOn
    });
    updatePowerAndLightUi(controllerState.lastSnapshot);
  });

  const dockingDistance = document.createElement('p');
  dockingDistance.className = 'docking-distance-readout dk-hud';
  dockingDistanceLabel = dockingDistance;
  dockingCard.append(dockingDistance);

  const dockedButton = document.createElement('button');
  dockedButton.type = 'button';
  dockedButton.className = 'secondary';
  const isDocked = controllerState.lastSnapshot?.submarine.docked ?? false;
  const dockingInRange = controllerState.lastSnapshot ? isDockingInRange(controllerState.lastSnapshot) : false;
  const canDock = isDocked || dockingInRange;
  dockedButton.textContent = isDocked ? 'Undock' : 'Dock';
  dockedButton.disabled = !canDock;
  dockedButton.title = isDocked
    ? 'Undock from the current docking port.'
    : `Move within ${DOCKING_ACTIVATION_RANGE_METERS}m of a docking port to dock.`;
  dockedButton.addEventListener('click', () => {
    if (!controllerState.currentRoomCode || !controllerState.lastSnapshot || dockedButton.disabled) {
      return;
    }

    const cameraOrigin = getDockingCameraOriginMapPoint(controllerState.lastSnapshot);
    emitControl({
      action: 'toggle_docked',
      cameraOriginX: cameraOrigin.x,
      cameraOriginZ: cameraOrigin.z,
      cameraOriginY: cameraOrigin.y
    });
    updatePowerAndLightUi(controllerState.lastSnapshot);
  });
  dockedToggleButton = dockedButton;
  setDockingUiElements({ dockingLightsButton, dockedToggleButton, dockingDistanceLabel, dockingViewportElement });
  // Sync labels now: the room may be entered before the first snapshot arrives.
  updatePowerAndLightUi(controllerState.lastSnapshot);

  const saveButton = document.createElement('button');
  saveButton.type = 'button';
  saveButton.className = 'primary';
  saveButton.textContent = 'Save game';
  saveButton.disabled = !isDocked;
  saveButton.title = isDocked
    ? 'Save the current game while docked.'
    : 'Save is only available while submarine is docked.';
  saveButton.addEventListener('click', () => {
    saveGameWithFeedback(getRevealedNodes);
  });
  saveGameButton = saveButton;

  dockingCard.append(dockingViewport);
  controls.append(dockingCard);

  if (ctrlZoneLeftBottom) {
    ctrlZoneLeftBottom.append(dockingToggle);
  }
  if (ctrlZoneRightAux) {
    ctrlZoneRightAux.append(dockedButton, saveButton);
  }

  bottomHullCameraView?.dispose();
  bottomHullCameraView = new BottomHullCameraView(dockingViewport, graphicsQuality, controllerRenderTuning, activePreset.fps);
  if (controllerState.lastRoomState) {
    bottomHullCameraView.setRoomState(controllerState.lastRoomState);
  }
  if (controllerState.lastSnapshot) {
    if (!controllerState.lastSnapshot.submarine.docked) {
      bottomHullCameraView.setSnapshot(controllerState.lastSnapshot);
    }
    if (dockingDistanceLabel) {
      const approach = describeDockingApproach(controllerState.lastSnapshot);
      dockingDistanceLabel.style.display = 'block';
      dockingDistanceLabel.textContent = approach.text;
      dockingDistanceLabel.dataset.state = approach.state;
    }
    updateDockingViewportDockedState(controllerState.lastSnapshot.submarine.docked);
  }
};

let lastRenderedRole: Role | null = null;
let lastRenderedRoomCode = '';

const renderControls = (): void => {
  if (!controls) {
    return;
  }

  const roleForUi = normalizeControllerRoleForUi(controllerState.currentRole);

  const rightAuxRoles: Set<Role> = new Set([
    'navigation',
    'storage',
    'sonar',
    'periscope',
    'docking',
    'engine',
    'mining',
    'battery',
    'life_support'
  ]);
  const leftBottomRoles: Set<Role> = new Set([
    'battery',
    'captain',
    'engine',
    'docking',
    'life_support'
  ]);

  const rightAuxRequired = roleForUi ? rightAuxRoles.has(roleForUi) : false;
  const leftBottomRequired = roleForUi ? leftBottomRoles.has(roleForUi) : false;
  const rightAuxFilled = ctrlZoneRightAux ? ctrlZoneRightAux.childElementCount > 0 : true;
  const leftBottomFilled = ctrlZoneLeftBottom ? ctrlZoneLeftBottom.childElementCount > 0 : true;

  if (
    controllerState.currentRole === lastRenderedRole &&
    controllerState.currentRoomCode === lastRenderedRoomCode &&
    controls.childElementCount > 0 &&
    (!rightAuxRequired || rightAuxFilled) &&
    (!leftBottomRequired || leftBottomFilled)
  ) {
    return;
  }

  lastRenderedRole = controllerState.currentRole;
  lastRenderedRoomCode = controllerState.currentRoomCode;
  navigationViewportLayoutCleanup?.();
  navigationViewportLayoutCleanup = null;

  // Set sonar range update callback to refresh zone displays
  setSonarRangeUpdateCallback(() => {
    updateMissionZoneReadouts(controllerState.lastRoomState);
  });

  // Only dispose role-specific WebGL views when switching away from that role.
  const isHarpoonRole = roleForUi === 'mining';
  const isDockingRole = roleForUi === 'docking';

  if (roleForUi !== 'sonar') {
    if (controllerMissionScreen) {
      controllerMissionScreen.classList.remove('sonar-role');
    }
  }
  if (!isHarpoonRole) {
    harpoonCameraView?.dispose();
    harpoonCameraView = null;
  }
  if (roleForUi !== 'drone') {
    disposeDroneControls();
  }
  if (roleForUi !== 'weapons' && roleForUi !== 'weapons_aft') {
    disposeWeaponsControls();
  }
  pilotDepthGauge = null;
  pilotDepthGaugeValue = null;
  pilotDepthGaugeCaption = null;
  pilotDepthGaugeValue = null;
  pilotDepthGaugeCaption = null;
  pilotSpeedGauge = null;
  pilotSpeedGaugeValue = null;
  pilotSpeedGaugeCaption = null;
  pilotHeadingGauge = null;
  pilotHeadingGaugeValue = null;
  pilotHeadingGaugeCaption = null;
  captainDepthGauge = null;
  if (!isDockingRole) {
    bottomHullCameraView?.dispose();
    bottomHullCameraView = null;
    if (controllerMissionScreen) {
      controllerMissionScreen.classList.remove('docking-role');
    }
  }
  captainDepthGaugeValue = null;
  captainDepthGaugeCaption = null;
  captainSpeedGauge = null;
  captainSpeedGaugeValue = null;
  captainSpeedGaugeCaption = null;
  captainChecklistDepth = null;
  captainChecklistWaypoint = null;
  captainChecklistHull = null;
  captainRoleAssignments = null;
  stopBatteryAnimation();
  disposeEngineerView();
  controls.innerHTML = '';
  controls.classList.remove('pilot-controls-layout', 'camera-role-layout', 'navigation-role-layout', 'captain-role-layout', 'sonar-controls-layout', 'battery-controls-layout', 'engine-controls-layout', 'ballast-role-layout', 'storage-role-layout', 'facility-role-layout', 'station-layout', 'drone-room-layout');
  if (ctrlZoneLeftTop) {
    ctrlZoneLeftTop.innerHTML = '';
    // The wipe above orphans every cached readout span; without this a same-role
    // re-render skipped the rebuild and left the top-left strip permanently blank.
    lastZoneRole = null;
  }
  if (ctrlZoneLeftBottom) {
    ctrlZoneLeftBottom.innerHTML = '';
  }
  if (ctrlZoneRightAux) {
    ctrlZoneRightAux.innerHTML = '';
  }

  if (!controllerState.currentRole) {
    controls.textContent = 'Choose a role to unlock role-specific controls.';
    updateMissionZoneReadouts(controllerState.lastRoomState);
    updateRoomHeader(controllerState.lastRoomState);
    return;
  }

  // Apply role-specific layout classes
  if (controllerMissionScreen) {
    controllerMissionScreen.classList.toggle('pilot-role', roleForUi === 'pilot');
    controllerMissionScreen.classList.toggle('sonar-role', roleForUi === 'sonar');
    controllerMissionScreen.classList.toggle('periscope-role', roleForUi === 'periscope');
    controllerMissionScreen.classList.toggle('docking-role', roleForUi === 'docking');
  }

  // Facility (base) rooms render long lists that must scroll rather than clip.
  controls.classList.toggle('facility-role-layout', roleForUi?.startsWith('facility_') ?? false);

  const roleControlRenderers: Partial<Record<Role, () => void>> = {
    pilot: renderPilotControls,
    ballast: renderBallastControls,
    sonar: renderSonarControls,
    docking: renderDockingControls,
    navigation: renderNavigationControls,
    comms: renderCommsControls,
    captain: renderCaptainControls,
    engine: renderEngineControls,
    life_support: renderLifeSupportControls,
    engineer: renderEngineerControls,
    storage: renderStorageControls,
    drone: renderDroneControls,
    battery: renderBatteryControls,
    harpoon: renderHarpoonControls,
    weapons: renderWeaponsControls,
    weapons_aft: renderWeaponsAftControls,
    periscope: renderPeriscopeControls,
    quarters: renderQuartersControls,
    facility_upgrades: () => facilityUpgrades.render(),
    facility_shop: () => facilityShop.render(),
    facility_missions: () => facilityMissions.render(),
    facility_repair: () => facilityRepair.render()
  };

  const renderer = roleForUi ? roleControlRenderers[roleForUi] : undefined;
  if (renderer) {
    updateMissionZoneReadouts(controllerState.lastRoomState);
    renderer();
    updateRoomHeader(controllerState.lastRoomState);
    return;
  }

  updateStorageProcessingLoopState(controllerState.lastRoomState ?? null);

  controls.textContent = `Controls are not available for ${formatRoleLabel(controllerState.currentRole)} yet.`;
  updateMissionZoneReadouts(controllerState.lastRoomState);
  updateRoomHeader(controllerState.lastRoomState);
};

// Crew quarters: no controls, just bunks. The room exists as flood ballast (engineer's flood valves).
let quartersStatusEl: HTMLElement | null = null;
const updateQuartersPanel = (snapshot: GameSnapshot | null): void => {
  if (!quartersStatusEl || !snapshot) return;
  const flooded = Math.round(snapshot.submarine.roomFlood?.quarters ?? 0);
  quartersStatusEl.textContent = flooded > 0 ? `Compartment flooded: ${flooded}%` : 'Compartment dry. All quiet.';
};
const renderQuartersControls = (): void => {
  const card = createCard('Crew Quarters', 'Bunks and lockers.');
  card.classList.add('ctrl-room-main-card');
  const flavour = document.createElement('p');
  flavour.textContent = 'Eight bunks, six lockers and one kettle that has seen better days. The crew sleeps in shifts; nobody has ever slept well. '
    + 'There is nothing to operate here. If Engineering opens the flood valves, you will be told to leave before the water comes in.';
  quartersStatusEl = document.createElement('div');
  quartersStatusEl.className = 'section-title';
  quartersStatusEl.dataset.quartersStatus = 'true';
  card.append(flavour, quartersStatusEl);
  controls?.append(card);
  updateQuartersPanel(controllerState.lastSnapshot ?? null);
};

// Each roomState broadcast bumps this; renderRoomState awaits map fetches, so an
// older broadcast can finish after a newer one. Stale ones must bail out, or
// they'd overwrite the newer role (e.g. a "walking, no role" state landing after
// the walk-arrival state leaves the player stuck outside the room).
let renderRoomStateSeq = 0;

// ── Launch countdown ─────────────────────────────────────────────────────────
// When any crew member presses "Start now" the server sets pendingGameStartAt; the TV
// shows a launch countdown and so should every phone. Only the Cancel button takes input.
let launchCountdownEl: HTMLElement | null = null;
let launchCountdownNumEl: HTMLElement | null = null;
let launchCountdownEndAt: number | null = null;
let launchCountdownTimer: ReturnType<typeof setInterval> | null = null;

const renderLaunchCountdown = (): void => {
  if (launchCountdownEndAt === null || !launchCountdownNumEl) return;
  // Phone clocks drift from the server's: never show more than the countdown's full length.
  const remainingSec = Math.min(
    Math.ceil(GAME_START_COUNTDOWN_MS / 1000),
    Math.max(0, Math.ceil((launchCountdownEndAt - Date.now()) / 1000))
  );
  if (launchCountdownNumEl.textContent !== String(remainingSec)) {
    launchCountdownNumEl.textContent = String(remainingSec);
    // Restart the pulse so each number lands with the same beat.
    launchCountdownNumEl.classList.remove('tick');
    void launchCountdownNumEl.offsetWidth;
    launchCountdownNumEl.classList.add('tick');
  }
};

const updateLaunchCountdown = (pendingAt: number | null | undefined, phase: string): void => {
  const active = !isEmbeddedPlayer && phase !== 'running' && typeof pendingAt === 'number' && pendingAt - Date.now() > -1000;
  if (active) {
    if (!launchCountdownEl) {
      launchCountdownEl = document.createElement('div');
      launchCountdownEl.className = 'launch-countdown';
      launchCountdownEl.setAttribute('aria-live', 'polite');
      const label = document.createElement('div');
      label.className = 'launch-countdown-label';
      label.textContent = 'Launching in';
      launchCountdownNumEl = document.createElement('div');
      launchCountdownNumEl.className = 'launch-countdown-num';
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'launch-countdown-cancel';
      cancelBtn.textContent = 'Cancel';
      cancelBtn.addEventListener('click', () => {
        if (controllerState.currentRoomCode) network?.cancelStart(controllerState.currentRoomCode);
      });
      launchCountdownEl.append(label, launchCountdownNumEl, cancelBtn);
      document.body.append(launchCountdownEl);
    }
    launchCountdownEndAt = pendingAt as number;
    launchCountdownEl.hidden = false;
    renderLaunchCountdown();
    if (launchCountdownTimer === null) launchCountdownTimer = setInterval(renderLaunchCountdown, 200);
  } else {
    launchCountdownEndAt = null;
    if (launchCountdownTimer !== null) { clearInterval(launchCountdownTimer); launchCountdownTimer = null; }
    if (launchCountdownEl) launchCountdownEl.hidden = true;
  }
};

// ── Lobby screen ─────────────────────────────────────────────────────────────
// Mission/chapter title, crew + rooms and the Start now button. Entering a room does not start the game.
const lobbyMissionTitleEl = document.querySelector<HTMLElement>('#lobbyMissionTitle');
const lobbyMissionSubEl = document.querySelector<HTMLElement>('#lobbyMissionSub');
const lobbyCrewListEl = document.querySelector<HTMLElement>('#lobbyCrewList');
const lobbyStartActionEls = document.querySelectorAll<HTMLElement>('.lobby-start-actions');
document.querySelectorAll<HTMLButtonElement>('.lobby-start-now-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    if (controllerState.currentRoomCode) network?.startNow(controllerState.currentRoomCode);
  });
});

const renderLobbyInfo = (roomState: RoomState): void => {
  const inLobby = roomState.phase !== 'running';
  const counting = typeof roomState.pendingGameStartAt === 'number';
  lobbyStartActionEls.forEach((el) => { el.hidden = !inLobby || counting; });
  if (!inLobby) return;
  const campaign = roomState.campaign;
  const mapName = getMapByMissionId(roomState.mission)?.name ?? roomState.mission;
  if (lobbyMissionTitleEl) lobbyMissionTitleEl.textContent = campaign?.chapterTitle || mapName || 'Lobby';
  if (lobbyMissionSubEl) {
    const sub = campaign ? `${campaign.campaignName} \u00b7 Chapter ${campaign.chapterIndex + 1} of ${campaign.chapterCount}` : '';
    lobbyMissionSubEl.textContent = sub;
    lobbyMissionSubEl.hidden = !sub;
  }
  if (lobbyCrewListEl) {
    lobbyCrewListEl.replaceChildren();
    for (const player of roomState.players) {
      if (player.connected === false) continue;
      const li = document.createElement('li');
      if (player.id === controllerState.currentPlayerId) li.classList.add('me');
      const name = document.createElement('span');
      name.textContent = player.name || 'Crew';
      const role = document.createElement('span');
      role.className = `lobby-crew-role${player.role ? '' : ' unassigned'}`;
      role.textContent = player.role ? formatRoleLabel(player.role) : 'choosing\u2026';
      li.append(name, role);
      lobbyCrewListEl.append(li);
    }
  }
};

const renderRoomState = async (roomState: RoomState, forceMapReload = false): Promise<void> => {
  const renderSeq = ++renderRoomStateSeq;
  const previousRoomState = controllerState.lastRoomState;
  const roomChanged = previousRoomState && previousRoomState.code !== roomState.code;
  const missionChanged = previousRoomState && previousRoomState.mission !== roomState.mission;
  const upgradesChanged = previousRoomState &&
    JSON.stringify(previousRoomState.ownedUpgrades) !== JSON.stringify(roomState.ownedUpgrades);
  controllerState.lastRoomState = roomState;
  updateLaunchCountdown(roomState.pendingGameStartAt, roomState.phase);
  renderLobbyInfo(roomState);
  updateCastQualityButtons(roomState.castQuality ?? 'medium');
  castSetup.notifyRoomState(roomState);
  // The hidden in-game player shares the phone with the host page's stream
  // renderer, but its own castSetup never starts that renderer, so the usual
  // "streaming" callback never fires. Follow the room's cast mode instead —
  // otherwise this view competes at full quality with the canvas feeding the TV
  // (field logs: sender fps collapsing to 4-8 once the player was revealed).
  if (isEmbeddedPlayer) setCastRenderingScaleBack(roomState.castMode === 'phone-renders');
  // Reattach to a cast that survived page navigation (e.g. app → controller
  // destroys an active phone-render stream; restart it here). No-op once
  // restored; waits until the room code is known. Skipped when embedded as the
  // host's background player — the parent page still owns the live stream.
  if (!isEmbeddedPlayer) castSetup.restoreIfActive();
  const currentMap = getMapByMissionId(roomState.mission);
  const isCustomMap = currentMap?.id === roomState.mission;

  if (currentMap == null || (forceMapReload && isCustomMap)) {
    await loadCustomMapDefinition(roomState.mission, forceMapReload);
  }

  // Load collision mesh separately (stripped from main API to protect cast devices).
  // Awaited so it's ready before SonarScanView.setRoomState runs below.
  if (isCustomMap || currentMap == null) {
    await loadCustomMapCollisionMesh(roomState.mission);
  }

  if (renderSeq !== renderRoomStateSeq) {
    return; // a newer room state superseded this one while the map loaded
  }

  activeMap = getMapByMissionId(roomState.mission) ?? activeMap;

  // Warm the core exterior GLBs (submarine, terrain, base markers) in the
  // background so the first 3D controller view to mount renders almost instantly
  // instead of stalling for several seconds on the GLB fetch+parse. Deduped per
  // map + quality inside preloadCoreModels, so calling it on every room update is
  // cheap. Fire-and-forget — never blocks the UI.
  if (activeMap) {
    preloadCoreModels(activeMap, graphicsQuality);
  }

  // Reset node reveal state and load tile JSON when entering a new mission
  if (!previousRoomState || missionChanged) {
    resetNodeRevealState();
    if (activeMap) {
      void loadTileJsonForMap(activeMap);
    }
  }

  // Dispose WebGL views if we're switching to a different room
  const shouldUpdateRoomViews =
    !previousRoomState ||
    previousRoomState.mission !== roomState.mission ||
    forceMapReload;

  if (controllerState.currentRoomCode && roomState.code !== controllerState.currentRoomCode) {
    disposeRoomViews();
  }
  controllerState.currentRoomCode = roomState.code;
  controllerState.currentPhase = roomState.phase;
  if (shouldUpdateRoomViews) {
    setSonarRoomState(roomState);
    bottomHullCameraView?.setRoomState(roomState);
    setControllerRoomState(roomState);
  } else {
    // setControllerRoomState only runs on mission changes, but the storage room's
    // live UI (queue count, craft bars) must track every broadcast so crafting
    // feedback appears immediately.
    updateStorageRoomState();
  }
  renderRoleButtons(roomState, lobbyRoleButtons, controllerRoleTabs, createRoleButton);
  if (roomMapPanel && !roomMapPanel.hidden) {
    renderRoomMap(roomState, roomMapGrid, roomMapPanel, roomMapStatus, (role) => ROLE_META[role], handleRoleTap, roomMapTabs);
  }

  const previousRole = controllerState.currentRole;
  const currentPlayer = controllerState.currentPlayerId
    ? roomState.players.find((player) => player.id === controllerState.currentPlayerId)
    : null;

  if (currentPlayer) {
    controllerState.currentRole = currentPlayer.role;
  } else if (!controllerState.currentPlayerId) {
    controllerState.currentRole = null;
  }
  // If we have a current player id but room state temporarily omits that player,
  // preserve the existing role until a definitive role update arrives.

  updateControllerMeta();

  if (controllerState.currentRole !== previousRole || roomChanged || missionChanged || upgradesChanged) {
    renderControls();
  }
  if (controllerState.currentRole === 'facility_upgrades') facilityUpgrades.updatePanel();
  if (controllerState.currentRole === 'facility_shop') facilityShop.updatePanel();
  if (controllerState.currentRole === 'facility_repair') facilityRepair.updatePanel();
  if (controllerState.currentRole === 'facility_missions') facilityMissions.updatePanel();
  updateMissionZoneReadouts(roomState);
  updateRoomHeader(roomState);
  updateRoomRepairButton(roomState);
  persistSession(nameInput?.value.trim() ?? '');
  updateScreenState();
  if (!controllerSettingsPanel?.hidden) {
    updateSettingsMeta();
  }
  setRuntimeStatus(
    controllerState.currentPhase === 'running'
      ? controllerState.currentRole
        ? `${formatRoleLabel(controllerState.currentRole)} | Live`
        : 'Live | Pick role'
      : 'Lobby'
  );
  if (!controllerState.autoJoinPending) {
    setStatus(`Joined ${controllerState.currentRoomCode}. ${controllerState.currentPhase === 'running' ? 'Mission live.' : 'Waiting in lobby.'}`);
  }

  if (controllerHostClosedBanner) {
    controllerHostClosedBanner.hidden = true;
  }
};

const handleRoomClosed = (): void => {
  // The game is over: stop the TV stream and end the Chromecast session. Without this the phone kept
  // casting (and the TV kept showing the scene) until the app was force-closed.
  castSetup.cancelPrewarm();
  void castSetup.stop();
  // If the game was ended deliberately, the final snapshot (delivered just before
  // this roomClosed) carried a summary — keep it on screen as the end-of-game
  // screen instead of the plain "room closed" banner.
  const endSummary = controllerState.lastSnapshot?.gameSummary ?? null;
  clearStoredSession();
  disposeRoomViews();
  disposeRoomMapViews();
  stopBatteryAnimation();
  controllerState.currentRoomCode = '';
  controllerState.currentPhase = 'lobby';
  controllerState.currentRole = null;
  controllerState.lastSnapshot = null;
  controllerState.lastRoomState = null;
  updateControllerMeta();
  updateScreenState();
  if (endSummary) {
    showMissionSummary(endSummary, true);
    setStatus('Game ended.');
    setRuntimeStatus('');
    return;
  }
  if (controllerHostClosedBanner) {
    controllerHostClosedBanner.hidden = false;
    window.setTimeout(() => {
      if (controllerHostClosedBanner) controllerHostClosedBanner.hidden = true;
    }, 8000);
  }
  setStatus('Room closed by host. Enter a new room code to join.');
  setRuntimeStatus('');
};

const handleServerDisconnect = (reason: string): void => {
  // Only react when we were actually in a room; disconnect churn before joining
  // (or after an intentional teardown) should not throw the player into a
  // scary message. The room lives in server memory, so a lost connection means
  // the game is effectively over — send them back to the start of setup.
  if (!controllerState.currentRoomCode) {
    return;
  }

  // Keep the session: the server holds our seat for RECONNECT_GRACE_PERIOD_MS, and Socket.IO reconnects on its own. The
  // 'connect' handler (attemptAutoJoin) rejoins with the same token. Dropping the token here made the reconnect join as a
  // brand-new crew member while the old seat (and its single-occupancy role) stayed held by the ghost of the old one.
  if (reason !== 'io server disconnect' && controllerState.reconnectToken) {
    showControllerQuickToast('⚠ Connection lost — reconnecting…');
    setStatus('Connection lost — reconnecting…');
    return;
  }

  // The server kicked us (or there is no seat to resume): drop the stored session and go back to setup.
  clearStoredSessionToken();
  disposeRoomViews();
  disposeRoomMapViews();
  stopBatteryAnimation();
  controllerState.currentRoomCode = '';
  controllerState.currentPhase = 'lobby';
  controllerState.currentRole = null;
  controllerState.lastSnapshot = null;
  controllerState.lastRoomState = null;
  updateControllerMeta();
  updateScreenState();
  const message = 'Connection to the server was lost — the mission has ended. Enter a room code to start again.';
  showControllerQuickToast('⚠ Connection lost — mission ended');
  setStatus(message);
  setRuntimeStatus('');
  console.warn(`Controller socket disconnected: ${reason}`);
};

nextStepButton?.addEventListener('click', () => {
  const code = roomInput?.value.trim().toUpperCase() ?? '';
  if (code.length < 1) {
    setStatus('Enter the 4-letter room code.');
    return;
  }
  if (roomInput) roomInput.value = code;
  if (codeConfirmLabel) codeConfirmLabel.textContent = code;
  if (lobbyStep1) lobbyStep1.hidden = true;
  if (lobbyStep2) lobbyStep2.hidden = false;
  setStatus('');
  nameInput?.focus();
});

backStepButton?.addEventListener('click', () => {
  if (lobbyStep2) lobbyStep2.hidden = true;
  if (lobbyStep1) lobbyStep1.hidden = false;
  setStatus('Enter the room code to continue.');
  roomInput?.focus();
});

// Cast quality buttons in lobby step 3
const castQualityButtons = document.querySelectorAll<HTMLButtonElement>('.lobby-cast-quality-btn');

const updateCastQualityButtons = (quality: string): void => {
  castQualityButtons.forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.quality === quality);
  });
};

castQualityButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const quality = btn.dataset.quality as 'low' | 'medium' | 'high';
    if (!quality) return;
    network?.setCastQuality(quality);
    updateCastQualityButtons(quality);
  });
});

lobbyOpenRoomsButton?.addEventListener('click', () => {
  if (!controllerState.lastRoomState) return;
  initMapViewForRole();
  renderRoomMap(controllerState.lastRoomState, roomMapGrid, roomMapPanel, roomMapStatus, (role) => ROLE_META[role], handleRoleTap, roomMapTabs);
  toggleRoomMapPanel(true, roomMapPanel, roomMapStatus);
});

lobbyLeaveButton?.addEventListener('click', () => {
  network?.leaveRoom?.();
  controllerState.currentRoomCode = '';
  controllerState.currentPhase = 'lobby';
  controllerState.currentRole = null;
  controllerState.lastRoomState = null;
  updateControllerMeta();
  updateScreenState();
  setStatus('Enter a room code to join.');
});

joinButton?.addEventListener('click', () => {
  const code = roomInput?.value.trim().toUpperCase() ?? '';
  const name = nameInput?.value.trim() ?? '';

  if (!code) {
    if (lobbyStep2) lobbyStep2.hidden = true;
    if (lobbyStep1) lobbyStep1.hidden = false;
    setStatus('Enter a room code first.');
    return;
  }

  if (!name) {
    setStatus('Enter your name first.');
    nameInput?.focus();
    return;
  }

  autoOpenRoomMenuOnJoin = true;
  controllerState.currentPhase = 'lobby';
  updateScreenState();
  network?.joinRoom(code, name);
});

roomMenuButton?.addEventListener('click', () => {
  if (!controllerState.lastRoomState) {
    showControllerQuickToast('Join a room first to open room selection.');
    toggleRoomMapPanel(false, roomMapPanel, roomMapStatus);
    return;
  }

  const open = roomMapPanel?.hidden !== false ? true : false;
  if (open) {
    initMapViewForRole();
    renderRoomMap(controllerState.lastRoomState, roomMapGrid, roomMapPanel, roomMapStatus, (role) => ROLE_META[role], handleRoleTap, roomMapTabs);
  }

  toggleRoomMapPanel(open, roomMapPanel, roomMapStatus);
});

roomMapCloseButton?.addEventListener('click', () => {
  if (!controllerState.currentRoomCode) {
    return;
  }
  toggleRoomMapPanel(false, roomMapPanel, roomMapStatus);
});

roomMapPanel?.addEventListener('click', (event) => {
  if (event.target === roomMapPanel) {
    toggleRoomMapPanel(false, roomMapPanel, roomMapStatus);
  }
});

const closeHeaderMenu = (): void => {
  if (!controllerHeaderMenu || !controllerHeaderMenuToggle) return;
  controllerHeaderMenu.hidden = true;
  controllerHeaderMenuToggle.setAttribute('aria-expanded', 'false');
};

controllerHeaderMenuToggle?.addEventListener('click', (e) => {
  e.stopPropagation();
  if (!controllerHeaderMenu || !controllerHeaderMenuToggle) return;
  const isOpen = !controllerHeaderMenu.hidden;
  if (!isOpen && controllerMenuRoomCodeValue) {
    controllerMenuRoomCodeValue.textContent = controllerState.currentRoomCode || '----';
  }
  if (!isOpen) positionHeaderMenu();
  controllerHeaderMenu.hidden = isOpen;
  controllerHeaderMenuToggle.setAttribute('aria-expanded', String(!isOpen));
});

// The menu is wider than the narrow side panel it lives in, and that panel clips its contents, so the menu
// is positioned in viewport coordinates (position: fixed) just under the button, right edges aligned.
const positionHeaderMenu = (): void => {
  if (!controllerHeaderMenu || !controllerHeaderMenuToggle) return;
  const rect = controllerHeaderMenuToggle.getBoundingClientRect();
  controllerHeaderMenu.style.top = `${Math.round(rect.bottom + 4)}px`;
  controllerHeaderMenu.style.right = `${Math.max(4, Math.round(window.innerWidth - rect.right))}px`;
};
window.addEventListener('resize', () => closeHeaderMenu());

controllerHeaderMenu?.addEventListener('click', (e) => {
  // The read-only room-code header isn't an action — clicking it (e.g. to select
  // the code) shouldn't dismiss the menu.
  if ((e.target as HTMLElement).closest('.ctrl-header-menu-roomcode')) return;
  closeHeaderMenu();
});

document.addEventListener('click', (e) => {
  if (controllerHeaderMenu && !controllerHeaderMenu.hidden &&
      !controllerHeaderMenuToggle?.contains(e.target as Node)) {
    closeHeaderMenu();
  }
});

controllerSettingsButton?.addEventListener('click', () => {
  toggleSettingsPanel(controllerSettingsPanel?.hidden !== false);
});

controllerRoomHelpButton?.addEventListener('click', () => {
  if (!controllerState.currentRole) {
    showControllerQuickToast('Join a room first to view room help.');
    return;
  }
  toggleRoomHelpPanel(controllerRoomHelpPanel?.hidden !== false);
});

controllerRoomHelpClose?.addEventListener('click', () => {
  toggleRoomHelpPanel(false);
});

controllerRoomHelpPanel?.addEventListener('click', (event) => {
  if (event.target === controllerRoomHelpPanel) {
    toggleRoomHelpPanel(false);
  }
});

controllerSettingsClose?.addEventListener('click', () => {
  toggleSettingsPanel(false);
});

controllerSettingsPanel?.addEventListener('click', (event) => {
  if (event.target === controllerSettingsPanel) {
    toggleSettingsPanel(false);
  }
});

type AndroidCastBridge = {
  isCastAvailable?: () => boolean;
  requestCast?: () => void;
  setPendingCode?: (code: string) => void;
  sendCode?: (code: string) => void;
  isConnected?: () => boolean;
  endSession?: () => void;
};
type AndroidBridge = { share?: (text: string) => void };
const androidCast = (window as unknown as { AndroidCast?: AndroidCastBridge }).AndroidCast;
const androidBridge = (window as unknown as { Android?: AndroidBridge }).Android;

const shareCastLink = (url: string): void => {
  const nav = navigator as Navigator & { share?: (d: { url: string }) => Promise<void> };
  if (typeof androidBridge?.share === 'function') {
    androidBridge.share(url);
  } else if (typeof nav.share === 'function') {
    void nav.share({ url }).catch(() => {/* dismissed */});
  } else {
    void navigator.clipboard?.writeText(url).then(() => {
      showControllerQuickToast('✓ TV link copied');
    });
  }
};

// ── Scale back our own rendering while we're also rendering the TV ───────────
// A phone running a phone-renders cast drives two renderers at once. Keeping its
// own room view at full quality starved the captured canvas, which is the screen
// everyone is actually watching. While the stream is live we drop this device's
// on-screen views one quality tier and cap their frame rate; the saved device
// preset is never overwritten, so stopping the cast restores it exactly.
let castRenderingScaleBackActive = false;
// Adaptive quality (phoneRenderer.ts) asks for extra local relief via a BroadcastChannel.
let castLocalReliefActive = false;

/**
 * Apply a runtime quality/fps without touching DEVICE_PRESET_KEY (the user's
 * saved choice). GRAPHICS_QUALITY_KEY / ANTIALIAS_KEY are what SceneViewportShell
 * reads, so they still have to be written.
 */
const applyRuntimeGraphics = (quality: GraphicsQuality, fps: number, antialias: boolean): void => {
  window.localStorage.setItem(GRAPHICS_QUALITY_KEY, quality);
  window.localStorage.setItem(ANTIALIAS_KEY, antialias ? 'on' : 'off');
  graphicsQuality = quality;
  setGraphicsQuality(graphicsQuality);
  setUserFpsOverride(fps);
  // Re-mount the active room so any live 3D view is rebuilt at the new settings.
  renderControls();
};

/** Graphics for the phone's own views while it feeds a cast; harder-cut when relief is requested. */
const applyCastScaleBackGraphics = (): void => {
  const reduced =
    getPresetFor(activePreset.deviceType, stepDownQualityLevel(getPresetQualityLevel(activePreset)))
    ?? activePreset;
  const storedFps = parseInt(window.localStorage.getItem(PRESET_FPS_KEY) ?? '', 10);
  const currentFps = Number.isFinite(storedFps) ? storedFps : activePreset.fps;
  applyRuntimeGraphics(
    castLocalReliefActive ? 'low' : reduced.quality,
    castLocalReliefActive ? CASTING_LOCAL_RELIEF_FPS : Math.min(currentFps, CASTING_LOCAL_FPS_CAP),
    false
  );
};

const setCastRenderingScaleBack = (active: boolean): void => {
  if (active === castRenderingScaleBackActive) return;
  castRenderingScaleBackActive = active;

  if (active) {
    applyCastScaleBackGraphics();
    showControllerQuickToast('Phone graphics reduced while casting');
    return;
  }

  castLocalReliefActive = false;
  const storedFps = parseInt(window.localStorage.getItem(PRESET_FPS_KEY) ?? '', 10);
  applyRuntimeGraphics(
    activePreset.quality,
    Number.isFinite(storedFps) ? storedFps : activePreset.fps,
    activePreset.antialias
  );
};

// The cast renderer prioritises the TV picture: when it can't hold 30 fps it first
// asks this UI to shed more local load, and gives it back last once there is headroom.
if ('BroadcastChannel' in window) {
  try {
    const pressureChannel = new BroadcastChannel(CAST_PRESSURE_CHANNEL);
    pressureChannel.onmessage = (event: MessageEvent<{ type?: string; on?: boolean }>) => {
      if (event.data?.type !== 'local-relief') return;
      const on = event.data.on === true;
      if (on === castLocalReliefActive || !castRenderingScaleBackActive) return;
      castLocalReliefActive = on;
      applyCastScaleBackGraphics();
    };
  } catch {/* unsupported — no local relief */}
}

// Random atmospheric hull creaks while a mission is running (existing creaking MP3s).
startAtmosphericCreaks({
  isActive: () => controllerState.lastSnapshot?.phase === 'running' && !controllerState.lastSnapshot.gameSummary,
  getDepth: () => controllerState.lastSnapshot?.submarine.depth ?? 0,
});

// Shared cast-setup wizard / active-cast panel (same module as app.ts).
const castAdapter: CastSetupAdapter = {
  getRoomCode: () => controllerState.currentRoomCode ?? '',
  emit: (event, payload) => network?.emitEvent(event, payload),
  share: (url) => shareCastLink(url),
  navigate: (url) => { window.location.href = url; },
  nativeCast: typeof androidCast?.requestCast === 'function'
    ? {
        requestCast: (code) => {
          androidCast?.setPendingCode?.(code);
          // A Chromecast session can outlive the game it was started for (new
          // game, or a stop that never ended it). The chooser would do nothing for
          // an already-connected device, so re-target the receiver directly.
          let connected = false;
          try { connected = androidCast?.isConnected?.() ?? false; } catch {/* ignore */}
          if (connected && typeof androidCast?.sendCode === 'function') androidCast.sendCode(code);
          else androidCast?.requestCast?.();
        },
        resendCode: (code) => {
          try {
            if (androidCast?.isConnected?.() && typeof androidCast?.sendCode === 'function') androidCast.sendCode(code);
          } catch {/* bridge not ready */}
        },
        // The native bridge throws if the session is already gone; that must not abort the teardown.
        endSession: () => { try { androidCast?.endSession?.(); } catch {/* already ended */} },
        isConnected: () => {
          try { return androidCast?.isConnected?.() ?? false; } catch { return false; }
        },
      }
    : null,
  classes: {
    primaryBtn: 'ctrl-btn ctrl-btn-primary',
    secondaryBtn: 'ctrl-btn ctrl-btn-secondary',
    ghostBtn: 'cast-back-btn',
    statusText: 'controller-cast-status',
  },
  getSocket: () => network!.getSocket(),
  onLocalRenderingLoadChanged: (streaming) => setCastRenderingScaleBack(streaming),
  embedded: isEmbeddedPlayer,
};

const castSetup = mountCastSetup(controllerCastSetupMount ?? document.createElement('div'), castAdapter);

// Desktop Chrome/Edge: attach a web Cast sender so a PC controller can pick a
// Chromecast (PC renders + streams over WebRTC), matching the Android app.
// Skipped when the native Android bridge is present; no-op without Cast support.
if (!castAdapter.nativeCast) {
  void createWebCastSender({
    onConnected: () => castSetup.notifyNativeCastConnected(),
    onEnded: () => castSetup.notifyNativeCastEnded(),
  }).then((sender) => {
    if (sender) castAdapter.nativeCast = sender;
  });
}

(window as unknown as { __onCastConnected?: () => void }).__onCastConnected = () => {
  castSetup.notifyNativeCastConnected();
};
(window as unknown as { __onCastEnded?: () => void }).__onCastEnded = () => {
  castSetup.notifyNativeCastEnded();
};

const toggleCastPanel = (open: boolean): void => {
  if (!controllerCastPanel) return;
  controllerCastPanel.hidden = !open;
  if (open) castSetup.openWizard();
};

controllerCastButton?.addEventListener('click', () => {
  toggleCastPanel(true);
});

controllerCastClose?.addEventListener('click', () => {
  toggleCastPanel(false);
});

controllerCastPanel?.addEventListener('click', (event) => {
  if (event.target === controllerCastPanel) toggleCastPanel(false);
});

const applyGraphicsPreset = (preset: DevicePreset): void => {
  activePreset = preset;
  window.localStorage.setItem(DEVICE_PRESET_KEY, preset.id);
  window.localStorage.setItem(GRAPHICS_QUALITY_KEY, preset.quality);
  window.localStorage.setItem(ANTIALIAS_KEY, preset.antialias ? 'on' : 'off');
  window.localStorage.setItem(PRESET_FPS_KEY, String(preset.fps));
  if (preset.pixelRatioScaleOverride) {
    window.localStorage.setItem(PIXEL_RATIO_SCALE_KEY, String(preset.pixelRatioScaleOverride));
  } else {
    window.localStorage.removeItem(PIXEL_RATIO_SCALE_KEY);
  }
  // A cast running right now owns the effective quality; the preset the user just
  // picked is stored and takes effect when the cast stops.
  const effectiveQuality = castRenderingScaleBackActive
    ? (getPresetFor(preset.deviceType, stepDownQualityLevel(getPresetQualityLevel(preset))) ?? preset).quality
    : preset.quality;
  const effectiveFps = castRenderingScaleBackActive
    ? Math.min(preset.fps, CASTING_LOCAL_FPS_CAP)
    : preset.fps;
  window.localStorage.setItem(GRAPHICS_QUALITY_KEY, effectiveQuality);
  graphicsQuality = effectiveQuality;
  setGraphicsQuality(graphicsQuality);
  setUserFpsOverride(effectiveFps);
  renderGraphicsSettingsUi();
  // Re-render the active room so any mounted 3D view is recreated at the new quality.
  renderControls();
  if (activeMap) {
    preloadCoreModels(activeMap, graphicsQuality);
  }
  showControllerQuickToast(`${preset.label} — ${effectiveFps} fps`);
};

settingsDeviceTabs?.addEventListener('click', (event) => {
  const btn = (event.target as HTMLElement).closest('button');
  const device = btn?.dataset.device as DeviceType | undefined;
  if (!device || device === activePreset.deviceType) return;
  // Keep the quality tier when switching device type.
  const preset = getPresetFor(device, getPresetQualityLevel(activePreset)) ?? getPresetFor(device, 'medium');
  if (preset) applyGraphicsPreset(preset);
});

settingsQualityRow?.addEventListener('click', (event) => {
  const btn = (event.target as HTMLElement).closest('button');
  const level = btn?.dataset.level as QualityLevel | undefined;
  if (!level) return;
  const preset = getPresetFor(activePreset.deviceType, level);
  if (preset && preset.id !== activePreset.id) applyGraphicsPreset(preset);
});

settingsAntialias?.addEventListener('change', () => {
  window.localStorage.setItem(ANTIALIAS_KEY, settingsAntialias.value === 'on' ? 'on' : 'off');
  renderControls();
  showControllerQuickToast(`Antialiasing: ${settingsAntialias.value === 'on' ? 'On' : 'Off'}`);
});

settingsFps?.addEventListener('change', () => {
  const fps = parseInt(settingsFps.value, 10);
  window.localStorage.setItem(PRESET_FPS_KEY, settingsFps.value);
  setUserFpsOverride(fps);
  renderControls();
  showControllerQuickToast(`Frame rate: ${settingsFps.value} fps`);
});

settingsVibration?.addEventListener('change', () => {
  const v = settingsVibration.value;
  vibrationLevel = v === 'low' || v === 'off' ? v : 'high';
  window.localStorage.setItem(VIBRATION_KEY, vibrationLevel);
  showControllerQuickToast(`Vibration: ${vibrationLevel.charAt(0).toUpperCase() + vibrationLevel.slice(1)}`);
});

// TV volumes are shared by the whole crew (they travel through the room); this phone's volume is local.
let tvAudioEmitTimer: ReturnType<typeof setTimeout> | null = null;
const sendTvAudioLevels = (): void => {
  if (tvAudioEmitTimer !== null) clearTimeout(tvAudioEmitTimer);
  // Sliders fire continuously while dragging: send once they settle.
  tvAudioEmitTimer = setTimeout(() => {
    tvAudioEmitTimer = null;
    network?.setCastAudio(Number(settingsTvMusic?.value ?? 32) / 100, Number(settingsTvEffects?.value ?? 100) / 100);
  }, 160);
};
settingsTvMusic?.addEventListener('input', sendTvAudioLevels);
settingsTvEffects?.addEventListener('input', sendTvAudioLevels);
settingsPhoneVolume?.addEventListener('input', () => {
  setMixVolume('effects', Number(settingsPhoneVolume.value) / 100);
});

settingsDeviceAudio?.addEventListener('change', () => {
  deviceAudioEnabled = settingsDeviceAudio.value === 'on';
  window.localStorage.setItem(DEVICE_AUDIO_KEY, deviceAudioEnabled ? 'on' : 'off');
  if (!deviceAudioEnabled) {
    storageProcessingAudio?.pause();
  }
  showControllerQuickToast(`Device audio: ${deviceAudioEnabled ? 'On' : 'Off'}`);
});

settingsLanguage?.addEventListener('change', () => {
  setLanguage(settingsLanguage.value as Lang);
  window.location.reload();
});

let lastViewportLayoutSize = {
  width: window.innerWidth,
  height: window.innerHeight
};

const refreshLayoutAfterViewportChange = throttle(() => {
  if (!controllerMissionScreen?.classList.contains('controller-screen-active')) {
    return;
  }

  const nextWidth = window.innerWidth;
  const nextHeight = window.innerHeight;
  const sizeDelta = Math.abs(nextWidth - lastViewportLayoutSize.width) + Math.abs(nextHeight - lastViewportLayoutSize.height);
  if (sizeDelta < 64) {
    return;
  }

  lastViewportLayoutSize = { width: nextWidth, height: nextHeight };

  // Avoid full control rerender on viewport changes; components already handle resize internally.
  window.requestAnimationFrame(() => {
    window.dispatchEvent(new Event('resize'));
  });
}, 220);

window.addEventListener('orientationchange', refreshLayoutAfterViewportChange, { passive: true });
window.addEventListener('resize', refreshLayoutAfterViewportChange, { passive: true });
window.visualViewport?.addEventListener('resize', refreshLayoutAfterViewportChange);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeHeaderMenu();
    toggleSettingsPanel(false);
    toggleRoomMapPanel(false, roomMapPanel, roomMapStatus);
    closeControllerEventLogPanel();
  }
});

controllerEventLogButton?.addEventListener('click', () => {
  openControllerEventLogPanel();
});

controllerEventLogClose?.addEventListener('click', () => {
  closeControllerEventLogPanel();
});

controllerEventLogPanel?.addEventListener('click', (event) => {
  if (event.target === controllerEventLogPanel) {
    closeControllerEventLogPanel();
  }
});

// Exit Game → a popup of three ways out: leave (just you), restart (whole crew),
// or end the game (summary + close the room for everyone).
const closeExitGameDialog = (): void => controllerExitGameDialog?.setAttribute('hidden', '');

controllerExitGameButton?.addEventListener('click', () => {
  if (!controllerState.currentRoomCode) {
    showControllerQuickToast('Join a room first.');
    return;
  }
  controllerExitGameDialog?.removeAttribute('hidden');
});
controllerExitGameCancel?.addEventListener('click', closeExitGameDialog);
controllerExitGameDialog?.addEventListener('click', (event) => {
  if (event.target === controllerExitGameDialog) closeExitGameDialog();
});

controllerExitLeaveRoomButton?.addEventListener('click', () => {
  closeExitGameDialog();
  // Leave the room server-side (frees the role for the rest of the crew), forget
  // the session so it won't auto-rejoin, then return to the home screen.
  network?.leaveRoom();
  clearStoredSession();
  window.location.href = '/app.html';
});

controllerExitRestartButton?.addEventListener('click', () => {
  closeExitGameDialog();
  const code = controllerState.currentRoomCode;
  if (!code) {
    showControllerQuickToast('Not in a room.');
    return;
  }
  hideMissionSummary();
  network?.restartGame(code);
  showControllerQuickToast('Restarting mission…');
});

controllerExitEndGameButton?.addEventListener('click', () => {
  closeExitGameDialog();
  const code = controllerState.currentRoomCode;
  if (!code) {
    showControllerQuickToast('Not in a room.');
    return;
  }
  // The server sends the end-of-game summary snapshot, then closes the room;
  // handleRoomClosed shows that summary as the final screen for everyone.
  network?.endGame(code);
});

// The native Android app already runs immersive full-screen and its WebView blocks the Fullscreen API, so
// the menu entry does nothing there. `hidden` alone was not enough: the menu-item class sets its own
// display, which overrides the attribute, so the entry stayed visible. Remove it outright.
const insideAndroidApp = (() => {
  const bridges = window as unknown as Record<string, unknown>;
  return Boolean(bridges['Android'] || bridges['AndroidCast']);
})();
if (controllerFullscreenButton && insideAndroidApp) {
  controllerFullscreenButton.hidden = true;
  controllerFullscreenButton.style.setProperty('display', 'none', 'important');
  controllerFullscreenButton.remove();
}

controllerFullscreenButton?.addEventListener('click', () => {
  if (!document.fullscreenElement) {
    void document.documentElement.requestFullscreen().catch(() => {
      showControllerQuickToast('Fullscreen is blocked by this browser.');
    });
  } else {
    void document.exitFullscreen().catch(() => {
      /* ignore */
    });
  }
});

document.addEventListener('fullscreenchange', () => {
  if (!controllerFullscreenButton) {
    return;
  }

  controllerFullscreenButton.setAttribute(
    'aria-label',
    document.fullscreenElement ? 'Exit fullscreen' : 'Enter fullscreen'
  );
});

controllerTechTreeClose?.addEventListener('click', () => {
  closeTechTreePanel(controllerTechTreePanel);
});

controllerTechTreePanel?.addEventListener('click', (event) => {
  if (event.target === controllerTechTreePanel) {
    closeTechTreePanel(controllerTechTreePanel);
  }
});

// rAF-batched DOM render state — set by onStateSnapshot, flushed each frame
let pendingRenderSnapshot: GameSnapshot | null = null;
let renderRafId: number | null = null;

const flushSnapshotRender = (): void => {
  renderRafId = null;
  const snapshot = pendingRenderSnapshot;
  if (!snapshot) return;
  pendingRenderSnapshot = null;

  if (snapshot.gameSummary) {
    // Don't downgrade a terminal (room-closed) summary back to the restartable one.
    if (!summaryTerminal) showMissionSummary(snapshot.gameSummary);
  } else {
    hideMissionSummary();
  }

  updateSonarDepthDial(snapshot);
  updatePilotPanel(snapshot);
  updateCaptainPanel(snapshot);
  if (controllerState.currentRole === 'facility_repair') facilityRepair.updatePanel();
  if (controllerState.currentRole === 'ballast') updateBallastPanel(snapshot);
  if (controllerState.currentRole === 'engineer') updateEngineerPanel(snapshot);
  if (controllerState.currentRole === 'quarters') updateQuartersPanel(snapshot);
  updatePowerAndLightUi(snapshot);
  updateDockingSaveButtonState(snapshot);
  updateMissionZoneReadouts(controllerState.lastRoomState);
  updateScreenState();

  if (controllerPowerFailureBanner) {
    controllerPowerFailureBanner.hidden = !snapshot.submarine.powerFailure;
  }

  // Drive the lights-out flicker from snapshot state (not just the one-shot
  // power-failure event) so phones that reconnect mid-blackout still see it.
  if (snapshot.submarine.powerFailure && !lastPowerFailureState) {
    triggerPowerFailureLightsFlicker();
  }
  lastPowerFailureState = snapshot.submarine.powerFailure;

  if (controllerResourceAlertBanner) {
    const severities = getSubmarineAlertSeverities(snapshot.submarine);
    const bannerRole = controllerState.currentRole;
    // Critical resources page every station; warnings only nag the owning role.
    const entries = ALERT_RESOURCES
      .filter((resource) => severities[resource] === 'critical'
        || (severities[resource] === 'warning' && RESOURCE_OWNER_ROLE[resource] === bannerRole))
      .map((resource) => ({
        resource,
        severity: severities[resource],
        value: resource === 'battery' ? snapshot.submarine.batteryCharge
          : resource === 'hull' ? snapshot.submarine.hull
          : snapshot.submarine[resource]
      }));

    if (entries.length > 0) {
      const anyCritical = entries.some((entry) => entry.severity === 'critical');
      controllerResourceAlertBanner.textContent = entries
        .map((entry) => `${entry.severity === 'critical' ? '🔴' : '⚠'} ${entry.severity === 'critical' ? 'CRITICAL' : 'LOW'} ${RESOURCE_ALERT_LABEL[entry.resource]} ${Math.round(entry.value)}%`)
        .join('  ·  ');
      controllerResourceAlertBanner.classList.toggle('is-critical', anyCritical);
      controllerResourceAlertBanner.hidden = false;
    } else {
      controllerResourceAlertBanner.hidden = true;
    }
  }

  if (controllerReserveAirBanner) {
    const reserveTicks = snapshot.submarine.oxygenReserveTicksRemaining;
    if (reserveTicks !== null && reserveTicks !== undefined && reserveTicks > 0) {
      const totalSeconds = Math.ceil(reserveTicks * SIMULATION_INTERVAL_MS / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      controllerReserveAirBanner.textContent = `⚠ OXYGEN DEPLETED — RESERVE AIR ${minutes}:${String(seconds).padStart(2, '0')}`;
      controllerReserveAirBanner.hidden = false;
    } else {
      controllerReserveAirBanner.hidden = true;
    }
  }

  const role = controllerState.currentRole;
  const roomFloodPercent = role ? (snapshot.submarine.roomFlood?.[role] ?? 0) : 0;
  updateFloodOverlay(roomFloodPercent);
  updateTemperatureEffects(snapshot);

  const displayDepth = snapshot.submarine.depth + ((activeMap?.missionLayout?.submarineSpawn as any)?.startingDepthOffset ?? 0);
  const runtimeMessage = role
    ? `${formatRoleLabel(role)} | H:${snapshot.submarine.hull.toFixed(0)} D:${displayDepth.toFixed(0)}m`
    : `${snapshot.phase === 'running' ? 'Live' : 'Lobby'} | Pick role`;
  setRuntimeStatus(runtimeMessage);
  setStatus(`${snapshot.phase === 'running' ? 'Mission live' : 'Lobby'} | Hull ${snapshot.submarine.hull.toFixed(0)} | ${snapshot.lastInput}`);
};

// Story narration: compact subtitle bar; this phone plays the clip when device audio is on (like the hull/marker voices).
let localReplayLogId: string | null = null;
let localReplayAt = 0;
const phoneVoice = createVoiceLines({
  mode: 'phone',
  shouldPlayAudio: () => deviceAudioEnabled,
  onLogUnlocked: (log) => notifyCaptainLogUnlocked(log)
});
setLogReplayHandler((log) => {
  localReplayLogId = log.id;
  localReplayAt = Date.now();
  phoneVoice.speak({ id: log.id, speaker: log.speaker, text: log.body, audio: log.audio, priority: 1, logId: log.id }, true);
  const code = controllerState.currentRoomCode;
  if (code) network?.emitEvent(EVENTS.logReplay, { code, logId: log.id });
});
(window as unknown as { __narrativeTest?: unknown }).__narrativeTest = {
  voiceLine: (line: import('../shared/narrative.js').VoiceLinePayload) => phoneVoice.speak(line),
  // Test hook only: the real server adds the id to RoomState.campaign.logsFound in the same room state.
  logUnlocked: (payload: import('../shared/narrative.js').LogUnlockedPayload) => {
    const found = controllerState.lastRoomState?.campaign?.logsFound;
    if (found && !found.includes(payload.log.id)) found.push(payload.log.id);
    phoneVoice.logUnlocked(payload);
  }
};

network = createControllerNetwork({
  setStatus,
  showQuickToast: showControllerQuickToast,
  onConnect: attemptAutoJoin,
  onDisconnected: handleServerDisconnect,
  onRoomState: async (roomState: RoomState) => {
    const shouldOpenRooms = autoOpenRoomMenuOnJoin;
    autoOpenRoomMenuOnJoin = false;

    await renderRoomState(roomState);

    if (pendingAutoStart && controllerState.currentRoomCode && roomState.phase === 'lobby') {
      pendingAutoStart = false;
      network?.startGame(controllerState.currentRoomCode);
    }

    if (shouldOpenRooms) {
      if (controllerState.lastRoomState) {
        initMapViewForRole();
        renderRoomMap(controllerState.lastRoomState, roomMapGrid, roomMapPanel, roomMapStatus, (role) => ROLE_META[role], handleRoleTap, roomMapTabs);
        toggleRoomMapPanel(true, roomMapPanel, roomMapStatus);
      }
    }

    updateStorageProcessingLoopState(roomState);
  },
  onRoleAssigned: (payload: RoleAssignedPayload, wasAutoJoin: boolean) => {
    persistSession(nameInput?.value.trim() ?? '');
    if (controllerState.lastRoomState) {
      renderRoleButtons(controllerState.lastRoomState, lobbyRoleButtons, controllerRoleTabs, createRoleButton);
    }
    updateControllerMeta();
    renderControls();
    updateStorageProcessingLoopState(controllerState.lastRoomState ?? null);
    if (payload.restored || wasAutoJoin) {
      const code = controllerState.currentRoomCode || roomInput?.value || '';
      showReconnectBanner(`Reconnected to ${code}. ${payload.role ? `Role ${formatRoleLabel(payload.role)} restored.` : 'Session restored.'}`);
      setStatus(`Rejoined ${code}. Role restored.`);
    }
  },
  onStateSnapshot: (snapshot: GameSnapshot) => {
    const previousSnapshot = controllerState.lastSnapshot;
    if (previousSnapshot?.phase !== 'running' && snapshot.phase === 'running') {
      playedHullWarnings.clear();
    }
    const wasOverloaded = previousSnapshot?.submarine.engineOverloaded ?? false;
    controllerState.lastSnapshot = snapshot;
    controllerState.currentPhase = snapshot.phase;

    // Drone connection warnings are shown in every room, not just the drone room.
    updateDroneWarnings(snapshot, controllerState.currentPlayerId);

    // Immediate: WebGL camera feeds (need state each tick for smooth rendering)
    harpoonCameraView?.setSnapshot(snapshot);
    if (!snapshot.submarine.docked) {
      bottomHullCameraView?.setSnapshot(snapshot);
    }
    updateDockingViewportDockedState(snapshot.submarine.docked);
    setControllerSnapshot(snapshot);

    // Immediate: time-sensitive audio and haptics
    const waterSeverity = updateHullWaterLevel(snapshot.submarine.hull);
    updateHullAudio(snapshot.submarine.depth, waterSeverity, snapshot.submarine.verticalVelocity);
    playHullWarning(snapshot.submarine.hull);
    updateHullLeakFeedback(snapshot.submarine.hullBreaches ?? {}, snapshot.submarine.hull, snapshot.submarine.hullZones);
    updatePilotEngineSound(snapshot.submarine);

    if (snapshot.submarine.engineOverloaded && !wasOverloaded) {
      triggerEngineOverloadAlert();
    }

    const empEventTick = snapshot.submarine.empEventTick ?? null;
    if (typeof empEventTick === 'number' && empEventTick !== lastEmpEventTick) {
      lastEmpEventTick = empEventTick;
      triggerEmpEffects();
    }

    // Defer all DOM mutations to next animation frame so the browser can
    // batch style recalculation and paint. The server ticks at 100 ms but
    // display refresh is 16 ms, so we never accumulate more than one pending
    // frame — the rAF coalesces rapid ticks automatically.
    pendingRenderSnapshot = snapshot;
    if (renderRafId === null) {
      renderRafId = window.requestAnimationFrame(flushSnapshotRender);
    }
  },
  onRenderTuningUpdated: (tuning: RenderTuning) => {
    applyRenderTuning(tuning, true);
  },
  onSonarPulse: (payload: SonarPulsePayload) => {
    triggerSonarPulseEffects();
    if (!controllerState.lastSnapshot) {
      return;
    }
    const scanType = payload.scanType ?? 'terrain';
    const hits = captureSonarPingHitsForNavigation(controllerState.lastSnapshot, scanType);
    // Every scan type goes through the same rules (src/shared/maps/navReveal.ts): terrain = ray hits, subsurface = all within range, resource = none.
    appendCaptainNavigationHits(hits, controllerState.lastSnapshot, payload.rangeMeters, scanType);
  },
  onCollisionImpact: (payload: CollisionImpactPayload) => {
    const strength = Math.max(0, Math.min(1, payload.strength));
    if (payload.source === 'worm') {
      playWormAttackSound(strength);
      showControllerQuickToast(
        payload.attackDamage
          ? `Worm attack! Hull damage ${payload.attackDamage}.`
          : 'Worm attack!'
      );
    }

    const pulseMs = Math.round(30 + strength * 75);
    // While a phone-renders cast is up, the TV shows this impact a few hundred ms after the event
    // reaches the phone; hold the haptic so it lands with the picture instead of ahead of it.
    const delayMs = Date.now() - castLatencyAt < CAST_LATENCY_STALE_MS ? castLatencyMs : 0;
    const buzz = (): void => vibratePhone([pulseMs, 22, Math.round(pulseMs * 0.8)]);
    if (delayMs > 0) window.setTimeout(buzz, delayMs); else buzz();
  },
  onCastLatency: (ms: number) => { castLatencyMs = ms; castLatencyAt = Date.now(); },
  onPowerFailure: (_payload: PowerFailurePayload) => {
    playPowerDownSound();
    showControllerQuickToast('POWER FAILURE — battery depleted');
    vibratePhone([80, 30, 80, 30, 200]);
  },
  onMarkerSoundTriggered: (payload: MarkerSoundTriggeredPayload) => {
    if (deviceAudioEnabled) {
      const audio = new Audio(`/media/sounds/voice/${encodeURIComponent(payload.markerName)}.mp3`);
      applyEffectsVolume(audio, 0.72);
      audio.addEventListener('ended', () => { audio.src = ''; }, { once: true });
      void audio.play().catch(() => {
        /* ignore blocked playback until user gesture */
      });
    }
    if (payload.message) {
      showControllerQuickToast(payload.message);
    }
  },
  onHostEventLogEntry: appendControllerHostEventLogEntrySafe,
  onVoiceLine: (line) => {
    // The reply to our own Replay carries the log id: we already started it locally, so skip the echo.
    if (line.logId && line.logId === localReplayLogId && Date.now() - localReplayAt < 4000) return;
    phoneVoice.speak(line);
  },
  onLogUnlocked: (payload) => {
    phoneVoice.logUnlocked(payload);
  },
  onErrorMessage: (payload: { message: string }) => {
    if (payload.message.toLowerCase().includes('role')) {
      showControllerQuickToast('Role occupied.');
    }
    setStatus(payload.message);
  },
  onNodeRevealRestored: (payload: { nodeRevealState: string }) => {
    if (payload.nodeRevealState) {
      restoreNodeRevealState(payload.nodeRevealState);
    }
  },
  onRoomClosed: handleRoomClosed
});

setControllerNetwork(network);
setControllerEmit(emitControl);
setControllerRenderTuning(controllerRenderTuning);

